import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { NbTokenLocalStorage } from '@nebular/auth';
import { environment } from '../environments/environment';

import * as moment from 'moment';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FileuploadComponent } from './fileupload/fileupload.component';
import { ConfirmComponent } from './confirm/confirm.component';
import { MENU_ITEMS } from './pages/pages-menu';


@Injectable({
  providedIn: 'root',
})
export class ApiServiceService {
  baseUrl: string;
  baseUrlNode: string;

  headers;
  private internetStatusSubject = new BehaviorSubject<boolean>(navigator.onLine);
  internetStatus$ = this.internetStatusSubject.asObservable();
  isOnlineAccess;
  logobase64 = environment.logobase64;

  private pendingToasts: Array<() => void> = [];
  private toastFlushTimer: ReturnType<typeof setTimeout> | null = null;

  timezones = [
    "Pacific/Auckland",
    "Asia/Kolkata",
    "Pacific/Midway",
    "US/Samoa",
    "US/Hawaii",
    "US/Alaska",
    "US/Pacific",
    "America/Tijuana",
    "US/Arizona",
    "US/Mountain",
    "America/Chihuahua",
    "America/Mazatlan",
    "America/Mexico_City",
    "America/Monterrey",
    "Canada/Saskatchewan",
    "US/Central",
    "US/Eastern",
    "US/East-Indiana",
    "America/Bogota",
    "America/Lima",
    "America/Caracas",
    "Canada/Atlantic",
    "America/La_Paz",
    "America/Santiago",
    "Canada/Newfoundland",
    "America/Buenos_Aires",
    "Greenland",
    "Atlantic/Stanley",
    "Atlantic/Azores",
    "Atlantic/Cape_Verde",
    "Africa/Casablanca",
    "Europe/Dublin",
    "Europe/Lisbon",
    "Europe/London",
    "Africa/Monrovia",
    "Europe/Amsterdam",
    "Europe/Belgrade",
    "Europe/Berlin",
    "Europe/Bratislava",
    "Europe/Brussels",
    "Europe/Budapest",
    "Europe/Copenhagen",
    "Europe/Ljubljana",
    "Europe/Madrid",
    "Europe/Paris",
    "Europe/Prague",
    "Europe/Rome",
    "Europe/Sarajevo",
    "Europe/Skopje",
    "Europe/Stockholm",
    "Europe/Vienna",
    "Europe/Warsaw",
    "Europe/Zagreb",
    "Europe/Athens",
    "Europe/Bucharest",
    "Africa/Cairo",
    "Africa/Harare",
    "Europe/Helsinki",
    "Europe/Istanbul",
    "Asia/Jerusalem",
    "Europe/Kiev",
    "Europe/Minsk",
    "Europe/Riga",
    "Europe/Sofia",
    "Europe/Tallinn",
    "Europe/Vilnius",
    "Asia/Baghdad",
    "Asia/Kuwait",
    "Africa/Nairobi",
    "Asia/Riyadh",
    "Europe/Moscow",
    "Asia/Tehran",
    "Asia/Baku",
    "Europe/Volgograd",
    "Asia/Muscat",
    "Asia/Tbilisi",
    "Asia/Yerevan",
    "Asia/Kabul",
    "Asia/Karachi",
    "Asia/Tashkent",
    "Asia/Kathmandu",
    "Asia/Yekaterinburg",
    "Asia/Almaty",
    "Asia/Dhaka",
    "Asia/Novosibirsk",
    "Asia/Bangkok",
    "Asia/Jakarta",
    "Asia/Krasnoyarsk",
    "Asia/Chongqing",
    "Asia/Hong_Kong",
    "Asia/Kuala_Lumpur",
    "Australia/Perth",
    "Asia/Singapore",
    "Asia/Taipei",
    "Asia/Ulaanbaatar",
    "Asia/Urumqi",
    "Asia/Irkutsk",
    "Asia/Seoul",
    "Asia/Tokyo",
    "Australia/Adelaide",
    "Australia/Darwin",
    "Asia/Yakutsk",
    "Australia/Brisbane",
    "Australia/Canberra",
    "Pacific/Guam",
    "Australia/Hobart",
    "Australia/Melbourne",
    "Pacific/Port_Moresby",
    "Australia/Sydney",
    "Asia/Vladivostok",
    "Asia/Magadan",
    "Pacific/Fiji"
  ];



  /////////////////// Dashboard


  loginUser: BehaviorSubject<any>;
  sipoUser: BehaviorSubject<any>;



  constructor(
    private http: HttpClient,
    private authService: NbTokenLocalStorage,
    private toastrService: NbToastrService,
    private dialogService: NbDialogService
  ) {
    this.baseUrl = environment.baseUrl;
    this.baseUrlNode = environment.baseUrlNode;
    window.addEventListener('online', () => this.updateInternetStatus());
    window.addEventListener('offline', () => this.updateInternetStatus());

    this.loginUser = new BehaviorSubject<any>(this.getAccessUser());
    this.sipoUser = new BehaviorSubject<any>(
      this.safeParseJson(localStorage.getItem('sipo_user')),
    );
  }

  private isDomReadyForOverlays(): boolean {
    // Nebular overlays need a real browser DOM host (usually document.body).
    return typeof document !== 'undefined' && !!document.body;
  }

  private scheduleToastFlush(delayMs: number = 0): void {
    if (this.toastFlushTimer) return;
    this.toastFlushTimer = setTimeout(() => {
      this.toastFlushTimer = null;
      this.flushPendingToasts();
    }, delayMs);
  }

  private flushPendingToasts(): void {
    if (!this.pendingToasts.length) return;

    if (!this.isDomReadyForOverlays()) {
      this.scheduleToastFlush(50);
      return;
    }

    const queue = this.pendingToasts.splice(0, this.pendingToasts.length);
    const failed: Array<() => void> = [];

    for (const fn of queue) {
      try {
        fn();
      } catch (e) {
        // If overlay host still isn't ready (rare timing), retry shortly.
        failed.push(fn);
        console.warn('Toastr failed (will retry):', e);
      }
    }

    if (failed.length) {
      this.pendingToasts.unshift(...failed);
      this.scheduleToastFlush(100);
    }
  }

  private safeToast(fn: () => void): void {
    if (!this.isDomReadyForOverlays()) {
      this.pendingToasts.push(fn);
      this.scheduleToastFlush(50);
      return;
    }

    try {
      fn();
    } catch (e) {
      // Never allow a toast to break bootstrap/navigation.
      this.pendingToasts.push(fn);
      this.scheduleToastFlush(100);
      console.warn('Toastr failed (queued):', e);
    }
  }

  logout(){
    this.clearAccessUser(false);
    localStorage.removeItem('sipo_user');
    if(localStorage.getItem('sipo_master_token'))
    {
      localStorage.removeItem('sipo_master_token');
      localStorage.removeItem('sipo_switch_login');
    }
    localStorage.removeItem('staff_pin_verified');
    localStorage.removeItem('is_staff_login');
    localStorage.removeItem('admin_app_server_version');
    localStorage.removeItem('admin_app_local_version');
    this.updateLoggedUser();
    // this.updateSipoUser();
}


  updateLoggedUser() {
    this.loginUser.next(this.getAccessUser());

  }

  storeAccessUser(user: any, triggerUpdate: boolean = true): void {
    if (!user) {
      this.clearAccessUser(triggerUpdate);
      return;
    }

    try {
      const encoded = this.encodeBase64(JSON.stringify(user));
      localStorage.setItem('accessUser', encoded);
    } catch (error) {
      console.error('Failed to encode access user payload', error);
      localStorage.setItem('accessUser', JSON.stringify(user));
    }

    if (triggerUpdate) {
      this.updateLoggedUser();
    }
  }

  clearAccessUser(triggerUpdate: boolean = true): void {
    localStorage.removeItem('accessUser');
    if (triggerUpdate) {
      this.updateLoggedUser();
    }
  }

  getAccessUser(): any {
    return this.decodeAccessUserPayload(localStorage.getItem('accessUser'));
  }


  updateSipoUser() {
    this.sipoUser.next(JSON.parse(localStorage.getItem('sipo_user')) !== null ? JSON.parse(localStorage.getItem('sipo_user')) : null);
  }
  updateSipoUserKey(key) {
    if (key) {
      //console.log('Next Value :',key);
      this.sipoUser.next(key);
    }
    else {
    }
  }

  updateInternetStatus() {
    this.internetStatusSubject.next(navigator.onLine);
  }

  checkStaffLogin() {
    return this.http.get(this.baseUrl + 'checkStaffLogin', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  isStaffLogin() {
    if (localStorage.getItem('is_staff_login') !== null && localStorage.getItem('is_staff_login') == 'true') {
      return true;
    }
    else {
      return false;
    }
  }

  toastrSuccess(title: any, message: any) {
    this.safeToast(() => this.toastrService.success(title, message));
  }

  toastrError(title: any, message: any) {
    this.safeToast(() => this.toastrService.danger(title, message));
  }

  toastrWarning(title: any, message: any) {
    this.safeToast(() => this.toastrService.warning(title, message));
  }

  toastrInfo(title: any, message: any) {
    this.safeToast(() => this.toastrService.info(title, message));
  }

  openFileUpload(data: any) {
    return this.dialogService.open(FileuploadComponent, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      context: {
        data: data,
      },
    });
  }

  confirmbox(data: any) {
    return this.dialogService.open(ConfirmComponent, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      context: {
        type: data.type,
        alertmsg: data.alertmsg,
        btntrue: data.btntrue,
        btnfalse: data.btnfalse,
      },
    });
  }


  getdashboardTotal(from_date, to_date) {
    return this.http.get(this.baseUrl + 'dashboard?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getdashboardTSummaryV2(from_date, to_date,user_id=null) {
    let data={
      startDate: from_date,
      endDate: to_date,
      user_id:user_id
    };
    return this.http.post(this.baseUrl + 'dashboard_report_v3',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getSalesHistory(from_date, to_date,type,user_id=null) {
    let data={
      startDate: from_date,
      endDate: to_date,
      type:type,
      user_id:user_id
    };
    return this.http.post(this.baseUrl + 'sales_history',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getSalesStatistics(from_date, to_date,user_id=null) {
    let data={
      startDate: from_date,
      endDate: to_date,
      user_id:user_id
    };
    return this.http.post(this.baseUrl + 'sales_statistics',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getHourlySalesData(from_date, to_date,user_id=null) {
    let data={
      startDate: from_date,
      endDate: to_date,
      user_id:user_id
    };
    return this.http.post(this.baseUrl + 'hourly_sales',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getVoucherSalesReport(from_date, to_date) {
    let data={
      start_date: from_date,
      end_date: to_date,
    };
    return this.http.post(this.baseUrl + 'voucher_report_list',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }




  getExternalDeliveryReports(from_date, to_date) {
    return this.http.get(this.baseUrl + 'get_external_deliveries?from=' + from_date + '&to=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getDoorDashStatus(id) {
    return this.http.get(this.baseUrl + 'get_doordash_delivery_status?external_delivery_id='+id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getUberDirectOrderStatus(id) {
    return this.http.get(this.baseUrl + 'uber_direct_order_status?external_delivery_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  cancelDoorDashDelivery(id) {
    return this.http.get(this.baseUrl + 'cancel_doordash_delivery?external_delivery_id='+id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  testUberDirectCred(id, secret) {
    let payload={
      'client_id':id,
      'client_secret':secret
    }
    return this.http.post(this.baseUrl + 'test_uber_direct',payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  refreshdashboardTotal(from_date, to_date) {
    return this.http.get(this.baseUrl + 'refresh_dashboard?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getsalesBy(from_date, to_date) {
    return this.http.get(this.baseUrl + 'dashboard_salesby?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getticketsBy(from_date, to_date) {
    return this.http.get(this.baseUrl + 'dashboard_ticketstatus?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getHourlySales(from_date, to_date,hourly_gap) {
    return this.http.get(this.baseUrl + 'ticket_statistic_report?from_date=' + from_date + '&to_date=' + to_date+'&hourly_gap='+hourly_gap, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getSaleTotal() {
    return this.http.get(this.baseUrl + 'chart', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /////////////////// User

  getUserData() {
    return this.http.get(this.baseUrl + 'user', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getUserInfo() {
    return this.http.get(this.baseUrl + 'get_user_info', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  //usermetadata




  getStoreSettings() {
    return this.http.get(this.baseUrl + 'get_store_settings', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getUserMetaData() {
    return this.http.get(this.baseUrl + 'userMetadata_get', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getServiceHourConfig() {
    return this.http.get(this.baseUrl + 'get_service_hours_config', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateServiceHourConfig(data:any) {
    return this.http.post(this.baseUrl + 'service_hours_status_update',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  updateStoreMetaSettings(data: {
    name?: string; email?: string; description?: string; address?: string;
    mobile?: string; country?: string; city?: string; postcode?: string;
    streetname?: string; website?: string; tax?: string; gst_no?: string; timezone?: string;
  }) {
    return this.http.post(this.baseUrl + 'update_store_meta_settings', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUserMetaDataUpdate(add) {

    const formdata = new FormData();
    formdata.append('email', add.email);
    formdata.append('name', add.name);
    formdata.append('description', add.description);
    formdata.append('mobile', add.mobile);
    formdata.append('country', add.country);
    formdata.append('delivery_buffer_time', add.delivery_buffer_time);
    formdata.append('pickup_buffer_time', add.pickup_buffer_time);
    formdata.append('pre_min_day', add.pre_min_day);
    formdata.append('city', add.city);
    formdata.append('online_inactive_reason', add.online_inactive_reason);
    formdata.append('website', add.website);
    formdata.append('postal_code', add.postcode);
    formdata.append('street_name', add.streetname);
    formdata.append('pickup', add.pickup ? '1' : '0');
    formdata.append('is_preorder', add.is_preorder ? '1' : '0');
    formdata.append('online_order_active', add.online_order_active ? '1' : '0');
    formdata.append('delivery', add.delivery ? '1' : '0');
    formdata.append('table_reservation', add.tablereserve ? '1' : '0');
    formdata.append('tax', add.tax);
    formdata.append('online_payment_enable', add.online_payment_enable ? '1' : '0');
    formdata.append('surcharge_enable', add.surcharge_enable ? '1' : '0');
    formdata.append('surcharge_percent', add.surcharge_percent);
    formdata.append('surcharge_amount', add.surcharge_amount);
    formdata.append('gst_no', add.gst_no);
    formdata.append('tax_exclusive', add.tax_exclusive ? '1' : '0');
    formdata.append('hide_tax_label', add.hide_tax_label ? '1' : '0');
    formdata.append('timezone', add.timezone);
    formdata.append('online_theme_color', add.online_theme_color);
    formdata.append('alert_info', add.alert_info);
    formdata.append('google_analytics_code', add.google_analytics_code);
    formdata.append('facebook_pixels_code', add.facebook_pixels_code);

    return this.http.post(this.baseUrl + 'userMetadata_create', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getUserPermissions() {
    return this.http.get(this.baseUrl + 'userPermissions_get', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getOnlinePermission() {
    let sts;
    this.getUserPermissions().subscribe((res) => {
      sts = res['data']['condition']['online'];

    });

    if (sts == 'true') {
      return true;
    }
    else {
      return false;
    }
  }


  //servive hours

  // OpeningHours(e) {

  //   const formdata = new FormData();
  //   formdata.append('day', e.day);
  //   formdata.append('type', e.type);
  //   formdata.append('open_time', e.open);
  //   formdata.append('is_closed', e.closed ? '1' : '0');
  //   formdata.append('closing_time', e.close);


  //   return this.http.post(this.baseUrl + 'insert_service_hours', formdata, {
  //     headers: new HttpHeaders({
  //       Authorization: 'Bearer ' + this.authService.get().getValue(),
  //     }),
  //   });
  // }
  OpeningHours(e) {

    let val = {
      data: e
    }


    return this.http.post(this.baseUrl + 'add_service_hours', val, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deteleServiceHoursGroup(id) {

    let data = {
      "group_id": id
    };
    return this.http.post(this.baseUrl + 'delete_service_hour', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });

  }

  getServiceHours() {

    return this.http.get(this.baseUrl + 'get_service_hours', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  editServiceHours(data) {

    return this.http.post(this.baseUrl + 'edit_service_hours', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  orderServiceHours(data) {

    return this.http.post(this.baseUrl + 'order_service_hours', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteServiceHours(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'delete_service_hours', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  addCordinates(lon, lat, addrs) {
    const formdata = new FormData();
    formdata.append('longitude', lon);
    formdata.append('latitude', lat);
    formdata.append('address', addrs);
    return this.http.post(this.baseUrl + 'addCoordinates', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



  getCorrdinate() {

    return this.http.get(this.baseUrl + 'getCoordinates', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }








  getTableReservation() {
    return this.http.get(this.baseUrl + 'get_table_reservation', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateTableReservation(data) {
    let preorder = data.preOrder ? '1' : '0';
    let active = data.is_active ? '1' : '0';
    let table_booking_placed_mail_to_customer = data.table_booking_placed_mail_to_customer ? '1' : '0';
    const formdata = new FormData;

    formdata.append('minimum_seat', data.minGuests);
    formdata.append('maximum_seat', data.maxGuests);
    formdata.append('waiting_time', data.minTime);
    formdata.append('reservation_time', data.holdTable);
    formdata.append('day_inadvance', data.maxTime);
    formdata.append('is_preorder', preorder);
    formdata.append('is_active', active);
    formdata.append('inactive_reason', data.inactive_reason);
    formdata.append('start_time_buffer', data.start_time_buffer);
    formdata.append('end_time_buffer', data.end_time_buffer);
    formdata.append('slot_duration', data.slot_duration);
    formdata.append('table_booking_placed_mail_to_customer', table_booking_placed_mail_to_customer);
    return this.http.post(this.baseUrl + 'add_table_reservation', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getDeliveryZones() {
    return this.http.get(this.baseUrl + 'get_delivery_zone', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  addDeliveryZones(data) {
    const formdata = new FormData;

    formdata.append('name', data.name);
    formdata.append('zone_area', data.area);
    formdata.append('minimum_amount', data.min);
    formdata.append('delivery_fee', data.fee);
    formdata.append('type', data.type);
    formdata.append('order_outside', '0');



    return this.http.post(this.baseUrl + 'add_delivery_zone', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateDeliveryZones(id, data) {
    const formdata = new FormData;

    formdata.append('id', id);
    formdata.append('name', data.name);
    formdata.append('zone_area', data.area);
    formdata.append('minimum_amount', data.min);
    formdata.append('delivery_fee', data.fee);
    formdata.append('type', data.type);
    formdata.append('order_outside', '0');



    return this.http.post(this.baseUrl + 'update_delivery_zone', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteDeliveryZone(id) {
    console.log(id);
    const formdata = new FormData;
    formdata.append('id', id);

    return this.http.post(this.baseUrl + 'delete_delivery_zone', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /////////////////// Reports

  getTerminalReport(start, end) {
    // const start_date = moment(start).startOf(s).format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).endOf(e).format('YYYY-MM-DD HH:mm:ss');

    // const start_date = moment(start).set({ "hour": stime.split(':')[0], "minute": stime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).set({ "hour": etime.split(':')[0], "minute": etime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');

    return this.http.get(
      this.baseUrl +
      'terminal_report?from_date=' +
      moment(start).utc().format() +
      '&&to_date=' +
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  getTerminalSummeryReport(start, end) {
   

   

    // let data={
    //   startDate: 1680307200000,
    //   endDate: 1726106002560
    // }
    let data={
      startDate: start,
      endDate: end
    }


    return this.http.post(
      this.baseUrl +
      'terminal_summary_report',data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getSalesSummaryReportDayWise(start, end) {
    let data = {
      startDate: start,
      endDate: end
    };

    return this.http.post(
      this.baseUrl + 'sales_summary_report_day_wise',
      data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }




  getTerminalReportUnified(data) {
   
    return this.http.post(
      this.baseUrl +
      'terminal_report_unified',data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getAllPaymentRecordsByDate(start, end) {
    return this.http.get(
      this.baseUrl +
      'get_all_records_by_date?from_date=' +
      moment(start).utc().format() +
      '&&to_date=' +
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  //Loyalty
  getAllLoyaltyCreditConfig() {
    return this.http.get(
      this.baseUrl +
      'restaurant-wallet-points-ratio-list' ,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  createLoyaltyCreditConfig(data) {
    return this.http.post(
      this.baseUrl +
      'restaurant-wallet-points-ratio-create',data ,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getLoyaltyProducts() {
    return this.http.get(
      this.baseUrl +
      'loyalty_product_list',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  updateLoyaltyProduct(data) {
    return this.http.post(
      this.baseUrl +
      'loyalty_product_update',data ,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  updateLoyaltyProductBulk(data) {
    return this.http.post(
      this.baseUrl +
      'loyalty_product_bulk_update',data ,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  updateLoyaltyCreditConfig(data,id) {
    return this.http.post(
      this.baseUrl +
      'restaurant-wallet-points-ratio-update/'+id,data ,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  deleteLoyaltyCreditConfig(id) {
    return this.http.get(
      this.baseUrl +
      'restaurant-wallet-points-ratio-delete/'+id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  bulkUpdateLoyaltyCreditConfig(data) {
    return this.http.post(
      this.baseUrl +
      'restaurant-wallet-points-ratio-create_bulk',data ,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }



   //Loyalty redumption 
   getAllLoyaltyRedumptionConfig() {
    return this.http.get(
      this.baseUrl +
      'restaurant-wallet-points-ratio-redumption-list' ,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  createLoyaltyRedumptionConfig(data) {
    return this.http.post(
      this.baseUrl +
      'restaurant-wallet-points-ratio-redumption-create',data ,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  updateLoyaltyRedumptionConfig(data,id) {
    return this.http.post(
      this.baseUrl +
      'restaurant-wallet-points-ratio-redumption-update/'+id,data ,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  deleteLoyaltyRedumptionConfig(id) {
    return this.http.get(
      this.baseUrl +
      'restaurant-wallet-points-ratio-redumption-delete/'+id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  bulkUpdateLoyaltyRedumptionConfig(data) {
    return this.http.post(
      this.baseUrl +
      'restaurant-wallet-points-ratio-redumption-create_bulk',data ,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }




  getTerminalReportByShift(from, to) {

    return this.http.get(
      this.baseUrl +
      'terminal_report_shift?from_date=' +
      from +
      '&&to_date=' +
      to,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getSalesReport(start: Date, end: Date) {
    // const start_date = moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    return this.http.get(
      this.baseUrl +
      'sales_report?from_date=' +
      moment(start).utc().format() +
      '&&to_date=' +
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getSalesReportCategory(start: Date, end: Date) {
    // const start_date = moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    return this.http.get(
      this.baseUrl +
      'sales_report_category?from_date=' +
      moment(start).utc().format() +
      '&&to_date=' +
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  itemSaleReportEmail(start: Date, end: Date, email: string) {
    // const start_date = moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const start_date = moment(start).utc().format();
    const end_date = moment(end).utc().format();
    let data = {
      'from_date': start_date,
      'to_date': end_date,
      'mailid': email
    };
    return this.http.post(
      this.baseUrl +
      'sales_report_category_pdf_email', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    )
  }
  itemSaleReportPdfExport(start: Date, end: Date) {
    // const start_date = moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const start_date = moment(start).utc().format();
    const end_date = moment(end).utc().format();
    let data = {
      'from_date': start_date,
      'to_date': end_date,
    };
    return this.http.post(
      this.baseUrl +
      'sales_report_category_pdf_export', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    )
  }


  
  getProductSalesByOrderType(type:any,start: any, end: any,aggregation:any) {
    
    console.log('check from',start);
    console.log('check to',end);

    const dateStringStart = start;
    const date = new Date(dateStringStart);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDateStart = `${year}-${month}-${day}`;

    const dateStringEnd = end;
    const dateend = new Date(dateStringEnd);
    const yearend = date.getFullYear();
    const monthend = String(dateend.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const dayend = String(dateend.getDate()).padStart(2, '0');
    const formattedDateEnd = `${yearend}-${monthend}-${dayend}`;
  

    return this.http.get(
      this.baseUrl +
      'product_sales_by_order_type?type='+type+'&from_date=' +
      formattedDateStart +
      '&to_date=' +
      formattedDateEnd+'&aggregation='+aggregation,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getProductSalesByOrderTypeExport(type:any,start: any, end: any,aggregation:any) {
    const dateStringStart = start;
    const date = new Date(dateStringStart);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDateStart = `${year}-${month}-${day}`;

    const dateStringEnd = end;
    const dateend = new Date(dateStringEnd);
    const yearend = date.getFullYear();
    const monthend = String(dateend.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const dayend = String(dateend.getDate()).padStart(2, '0');
    const formattedDateEnd = `${yearend}-${monthend}-${dayend}`;
    return this.http.get(
      this.baseUrl +'product_sales_by_order_type?type='+type+'&from_date='+
      formattedDateStart +
      '&to_date='+
      formattedDateEnd+'&aggregation='+aggregation,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }

  getCustomerJournalExcelExportCustomer(start: any, end: any,cid:any) {
    return this.http.get(
      this.baseUrl +'customer_journal_excel_export?from_date='+
      moment(start).utc().format() +
      '&to_date='+
      moment(end).utc().format()+'&customer_id='+cid,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }

  getCustomerJournalExcelExport(start: any, end: any) {
    return this.http.get(
      this.baseUrl +'customer_journal_excel_export?from_date='+
      moment(start).utc().format() +
      '&to_date='+
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }
  getCustomerJournalPdfExport(start: any, end: any) {
    return this.http.get(
      this.baseUrl +'customer_journal_pdf_export?from_date='+
      moment(start).utc().format() +
      '&to_date='+
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }
  getCustomerJournalPdfExportrCustomer(start: any, end: any,cid:any) {
    return this.http.get(
      this.baseUrl +'customer_journal_pdf_export_by_cust_id?from_date='+
      moment(start).utc().format() +
      '&to_date='+
      moment(end).utc().format()+'&customer_id='+cid,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }

  getCustomerJournalPdfBackend(start: any, end: any, cid: any,show_description: boolean) {
    // Format dates in UTC datetime format (same as get_customer_journals)
    const fromDate = moment(start).utc().format();
    const toDate = moment(end).utc().format();
    
    const payload = {
      customer_id: cid.toString(),
      from_date: fromDate,
      to_date: toDate,
      show_description: show_description
    };
    
    return this.http.post(
      this.baseUrl + 'generate-journal-pdf',
      payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
          'Content-Type': 'application/json'
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }

  emailCustomerJournalPdf(start: any, end: any, cid: any, show_description: boolean, recipient_email: string) {
    // Format dates in UTC datetime format
    const fromDate = moment(start).utc().format();
    const toDate = moment(end).utc().format();
    
    const payload = {
      customer_id: cid.toString(),
      from_date: fromDate,
      to_date: toDate,
      show_description: show_description,
      recipient_email: recipient_email
    };
    
    return this.http.post(
      this.baseUrl + 'email-journal-pdf',
      payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
          'Content-Type': 'application/json'
        })
      }
    );
  }

  


  getSalesReportModCount(start: Date, end: Date) {
    return this.http.get(
      this.baseUrl +
      'sales_report_mod_count?from_date=' +
      moment(start).utc().format() +
      '&&to_date=' +
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getSalesSummary(start: Date, end: any) {
    // const start_date = moment(start).set({ "hour": stime.split(':')[0], "minute": stime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).set({ "hour": etime.split(':')[0], "minute": etime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');
    return this.http.get(
      this.baseUrl +
      'sales_summary?from_date=' +
      moment(start).utc().format() +
      '&&to_date=' +
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  staffReport(start: Date, end: any) {
    return this.http.get(
      this.baseUrl +
      'staff_report?start_time=' +
      moment(start).utc().format() +
      '&&end_time=' +
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  salesSummaryReportEmail(start: Date, end: any, email: any) {
    let payload = {
      from_date: moment(start).utc().format(),
      to_date: moment(end).utc().format(),
      email: email
    };
    return this.http.post(
      this.baseUrl +
      'sales_summary_report', payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  salesSummaryV2ReportEmail(start: any, end: any, email: any) {
    let payload = {
     "startDate": start,
    "endDate": end,
      "email": email
    };
    return this.http.post(
      this.baseUrl +
      'sales_summary_v2_pdf_report_send_mail', payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  customerTransactionReportEmail(start: Date, end: any, email: any, cus_id: any) {
    let payload = {
      from_date: moment(start).utc().format(),
      to_date: moment(end).utc().format(),
      email: email,
      customer_id: cus_id
    };
    return this.http.post(
      this.baseUrl +
      'customer_transaction_report', payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  salesSummaryReportPdfExport(start: any, end: any) {
    let payload = {
      from_date: moment(start).utc().format(),
      to_date: moment(end).utc().format()
    };
    return this.http.post(
      this.baseUrl +
      'sales_summary_report_pdf_export', payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }
  ticketInvoicePdfExport(id:any) {
    let payload = {
      ticketId: id,
    };
    return this.http.post(
      this.baseUrl +
      'ticket_invoice_pdf_export', payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }
 ticketInvoiceHtmlView(id: any) {
  const payload = {
    ticketId: id,
    html: true
  };

  return this.http.post(
    this.baseUrl + 'ticket_invoice_pdf_export',
    payload,
    {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
      responseType: 'text' as 'json' // <-- This tells Angular to treat HTML as text
    }
  );
}

  salesSummaryV2ReportPdfExport(start: any, end: any) {
    let payload = {
      "startDate": start,
    "endDate": end
    };
    return this.http.post(
      this.baseUrl +
      'sales_summary_v2_pdf_report_export', payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }
  customerTransactionReportPdfExport(start: Date, end: any, cus_id: any) {
    let payload = {
      from_date: moment(start).utc().format(),
      to_date: moment(end).utc().format(),
      customer_id: cus_id
    };
    return this.http.post(
      this.baseUrl +
      'customer_transaction_report_pdf_export', payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
        responseType: 'blob',
        observe: 'response',
      },
    );
  }

  getMasterReport(start: Date, end: any) {

    // const start_date = moment(start).set({ "hour": stime.split(':')[0], "minute": stime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).set({ "hour": etime.split(':')[0], "minute": etime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');

    return this.http.get(
      this.baseUrl +
      'getPyamentRecords?from_date=' +
      moment(start).utc().format() +
      '&&to_date=' +
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getMasterReportMonth(index) {
    return this.http.get(
      this.baseUrl +
      'get_master_analytics_month?index=' + index,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getMasterReportWeek(index) {
    return this.http.get(
      this.baseUrl +
      'get_master_analytics_week?index=' + index,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getMasterReportWeekByDay(index, uid) {
    return this.http.get(
      this.baseUrl +
      'get_master_analytics_week_day?index=' + index + '&uid=' + uid,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getMasterReportDay(index) {
    return this.http.get(
      this.baseUrl +
      'get_master_analytics_day?index=' + index,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getSalesSummaryByShift(start: Date, end: any,) {
    return this.http.get(
      this.baseUrl +
      'sales_summary_by_shift?from_date=' +
      start +
      '&to_date=' +
      end,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getSalesFeed(start: any, end: any,type=null) {
    // const start_date = moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    let queryparams= 'sales_feed?from_date=' +moment(start).utc().format() +'&&to_date='+moment(end).utc().format();
    if(type != null || type !='')
    {
      queryparams=queryparams+'&&payment_type='+type;
    }
    return this.http.get(
      this.baseUrl +queryparams,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
    
  }

  salesTrendDaily(start: Date, end: Date) {
    // const start_date = moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    let data = {
      from_date: moment(start).utc().format(),
      to_date: moment(end).utc().format()
    };
    return this.http.post(
      this.baseUrl +
      'sales_summary_daily', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  getDiscountReport(from_date, to_date) {
    // let f = '2022-02-01T18:30:00Z';
    // let y='2022-02-02T18:29:59Z';


    return this.http.get(this.baseUrl + 'discountReport?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  // inventoryStockMoments(start:any, end:any) {
  //   return this.http.get(this.baseUrl + 'inventory_stock_moments?from_date=' + start + '&to_date=' + end , {
  //     headers: new HttpHeaders({
  //       Authorization: 'Bearer ' + this.authService.get().getValue(),
  //     }),
  //   });
  // }
  // inventoryStockMoments(start:any, end:any,movement_type:any,inventory_id=null) {
  //   if(movement_type && movement_type != 'all'){
  //     return this.http.get(this.baseUrl + 'inventory_stock_moments?from_date=' + moment(start).utc().format() + '&to_date=' + moment(end).utc().format() + '&movement_type=' + movement_type+'&inventory_id='+inventory_id, {
  //       headers: new HttpHeaders({
  //         Authorization: 'Bearer ' + this.authService.get().getValue(),
  //       }),
  //     });
  //   }
  //   else{
  //     return this.http.get(this.baseUrl + 'inventory_stock_moments?from_date=' +
  //       moment(start).utc().format() +
  //       '&&to_date=' +
  //       moment(end).utc().format(), {
  //       headers: new HttpHeaders({
  //         Authorization: 'Bearer ' + this.authService.get().getValue(),
  //       }),
  //     });
  //   }
    
  // }

  inventoryStockMoments(start: any, end: any, movement_type: any, inventory_id: any = null) {
    const formData = new FormData();
  
    // Always append start and end date
    formData.append('from_date', moment(start).utc().format());
    formData.append('to_date', moment(end).utc().format());
  
    // Conditionally append movement_type if not 'all'
    if (movement_type && movement_type !== 'all') {
      formData.append('movement_type', movement_type);
    }
  
    // Conditionally append inventory_id if provided
    if (inventory_id) {
      formData.append('inventory_id', inventory_id);
    }
  
    // Make a POST request with the form data
    return this.http.post(this.baseUrl + 'inventory_stock_moments', formData, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



  costReportByInventory() {
    return this.http.get(this.baseUrl + 'cost_report_by_inventory', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  costReportByCost() {
    return this.http.get(this.baseUrl + 'cost_report_by_cost', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  costReportBySale(start, end) {
    // const start_date = moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    return this.http.get(
      this.baseUrl +
      'cost_report_by_sale?from_date=' +
      moment(start).utc().format() +
      '&&to_date=' +
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  costReportSendEmail(email: string, userId: number, fromDate: Date, toDate: Date) {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('user_id', userId.toString());
    formData.append('from_date', moment(fromDate).utc().format('YYYY-MM-DD HH:mm:ss'));
    formData.append('to_date', moment(toDate).utc().format('YYYY-MM-DD HH:mm:ss'));

    return this.http.post(
      this.baseUrl + 'cost_report_send_email',
      formData,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  costReportBySaleHistory() {
    return this.http.get(
      this.baseUrl +
      'cost_report_by_sale_history',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }



  /////////////////// For Dropdowns

  getData(endPoint: string) {
    return this.http.get(this.baseUrl + endPoint, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


//// Super Categories
  getSuperCategories() {
    return this.http.get(this.baseUrl + 'category-menus/user', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createSuperCategories(val) {
    const formdata = new FormData();
    formdata.append('name', val.name);
    formdata.append('categories', val.categories);
    return this.http.post(this.baseUrl + 'category-menus', val, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editSuperCategories(val) {
    const formdata = new FormData();
    formdata.append("name", val.name);
    formdata.append("categories", val.categories);
    return this.http.put(this.baseUrl + 'category-menus/' + val.id, val, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteSuperCategories(val) {
    return this.http.delete(this.baseUrl + 'category-menus/' + val, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }




  /////////////////// Category

  createCategories(name, shortName, catDescription, catActive, inactiveReason, catHidden, image, showonly, exclude_online_table_menu,availability,color,course=null,av=false) {

    console.log('availability :',availability);
    console.log('availability  json:',JSON.stringify(availability));

    const formdata = new FormData();
    formdata.append('name', name);
    formdata.append('short_name', shortName);
    formdata.append('image', image);
    formdata.append('cat_description', catDescription);
    formdata.append('hide_from_menu', catHidden ? '1' : '0');
    formdata.append('inactive', catActive ? '1' : '0');
    formdata.append('inactive_reason', inactiveReason);
    formdata.append('show_only', showonly ? '1' : '0');
    formdata.append('exclude_online_table_menu', exclude_online_table_menu ? '1' : '0');
    formdata.append('availability', JSON.stringify(availability) );
    formdata.append('color', color);
    formdata.append('course', course);
    formdata.append('av', av ? '1' : '0');
    return this.http.post(this.baseUrl + 'category_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editCategories(id, name, shortName, catDescription, catActive, inactiveReason, catHidden, image, showonly, exclude_online_table_menu,availability,color,course=null,av=false) {

    // console.log('availability :',availability);
    // console.log('availability  json:',JSON.stringify(availability));
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    formdata.append('short_name', shortName);
    formdata.append('image', image);
    formdata.append('cat_description', catDescription);
    formdata.append('hide_from_menu', catHidden ? '1' : '0');
    formdata.append('inactive', catActive ? '1' : '0');
    formdata.append('inactive_reason', inactiveReason);
    formdata.append('show_only', showonly ? '1' : '0');
    formdata.append('exclude_online_table_menu', exclude_online_table_menu ? '1' : '0');
    formdata.append('availability', JSON.stringify(availability) );
    formdata.append('course', course);
    formdata.append('color', color);
    formdata.append('av', av ? '1' : '0');

    return this.http.post(this.baseUrl + 'category_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });

  }




  getCategories() {
    return this.http.get(this.baseUrl + 'categoryp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getAllCategories() {
    return this.http.get(this.baseUrl + 'category_all_get', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCategoriesPaginated(payload: {
    per_page?: number;
    page?: number;
    search?: string;
  }): Observable<any> {
    return this.http.post(this.baseUrl + 'categories_search_paginated', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  addCategoryAvailability(data) {


    return this.http.post(this.baseUrl + 'add_cat_availability', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteCategoryAvailability(id) {
    const formdata = new FormData();
    formdata.append('group_id', id);



    return this.http.post(this.baseUrl + 'cat_availability_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCategoryAvailability(id) {

    return this.http.get(this.baseUrl + 'get_cat_availability?cat_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCuisine() {
    return this.http.get(this.baseUrl + 'get_cuisine', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  addCuisine(name, image) {
    const formdata = new FormData();
    formdata.append('name', name);
    formdata.append('image', image);

    return this.http.post(this.baseUrl + 'add_cuisine', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  deleteCuisine(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'delete_cuisine', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getCuisineMapping() {
    return this.http.get(this.baseUrl + 'get_cuisine_user_map', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  addCuisineMapping(id) {
    const formdata = new FormData();
    formdata.append('cuisine_id', id);
    return this.http.post(this.baseUrl + 'add_cuisine_user_map', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteCuisineMapping(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'delete_cuisine_user_map', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }






  deleteCategory(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'category_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  //get-category-mapped

  getCategoryMappedEntities(id) {
    let payload = {
      'cat_id': id
    };
    return this.http.post(this.baseUrl + 'get-category-mapped', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCategoryMappedSummary(cat_id: string | number, limit: number = 10): Observable<any> {
    const payload = {
      cat_id: cat_id.toString(),
      limit: limit
    };
    return this.http.post(this.baseUrl + 'get-category-mapped-summary', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getModifierCategoryMappedEntities(id) {
    let payload = {
      'modifier_cat_id': id
    };
    return this.http.post(this.baseUrl + 'get-modf-cat-to-prod-cat', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getModifierMappedEntities(id) {
    let payload = {
      'modifier_id': id
    };
    return this.http.post(this.baseUrl + 'get-modifier-to-product-mapping', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getComboCatMappedEntities(id) {
    let payload = {
      "combo_cat_id": id
    }
    return this.http.post(this.baseUrl + 'get_combo_cat_mapped_data', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  deleteModifierCategoryMappedEntities(id) {

    let payload = {
      'modifier_cat_id': id
    };
    return this.http.post(this.baseUrl + 'delete-modf-cat-to-prod-cat', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  deleteModifierMappedEntities(id) {
    let payload = {
      'modifier_id': id
    };
    return this.http.post(this.baseUrl + 'delete-modifier-to-product-mapping', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteCategoryWithMappedEntities(id) {
    const formdata = new FormData();
    formdata.append('cat_id', id);
    return this.http.post(this.baseUrl + 'delete-category-mapped', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteCategoryMappedAsync(payload: {
    cat_id: number | string;
    batch_size?: number;
    queue?: string;
    delay?: number;
  }): Observable<any> {
    return this.http.post(this.baseUrl + 'delete-category-mapped-async', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCategoryBulkDeleteJobStatus(jobId: string): Observable<any> {
    return this.http.get(this.baseUrl + 'category/bulk-delete/status/' + jobId, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUserCategoryBulkDeleteJobs(): Observable<any> {
    return this.http.get(this.baseUrl + 'category/bulk-delete/jobs', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  //////////////// Subcategory

  createSubCategories(name: any, category_id,color='#ffffff',primary_course_id=null,av=0) {
    const formdata = new FormData();
    formdata.append('category_id', category_id);
    formdata.append('name', name);
    formdata.append('color', color);
    if (primary_course_id !== null && primary_course_id !== '') {
      formdata.append('primary_course_id', primary_course_id);
    }
    formdata.append('av', av ? '1' : '0');
    return this.http.post(this.baseUrl + 'subcategory_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getSubCategories() {
    return this.http.get(this.baseUrl + 'subcategoryp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProductsBySubCategory(id: any) {
    return this.http.get(this.baseUrl + 'get_products_by_subcategory?subcategory_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getSubCategoriesAndProductsByCategoryId(id: any) {
    return this.http.get(this.baseUrl + 'subcategory_by_category?cat_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getSubCategoriesById(id: any) {
    return this.http.get(this.baseUrl + 'subcategory_by_category?cat_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



  editSubCategories(id: any, name: any, cat_id,color='#ffffff',primary_course_id=null,av=0) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('cat_id', cat_id);
    formdata.append('name', name);
    formdata.append('color', color);
    if (primary_course_id !== null && primary_course_id !== '') {
      formdata.append('primary_course_id', primary_course_id);
    }
    formdata.append('av', av ? '1' : '0');
    return this.http.post(this.baseUrl + 'subcategory_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteSubCategory(id: any) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'subcategory_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  ///////////////////// Modifiercategory

  createModifierCategories(name, is_mandatory: any, min_modifier: any, max_modifier: any, allow_same: any, hide_online: any, availability=null) {
    const formdata = new FormData();
    formdata.append('name', name);
    formdata.append('is_mandatory', is_mandatory ? '1' : '0');
    formdata.append('min_modifier', min_modifier);
    formdata.append('max_modifier', max_modifier);
    formdata.append('allow_same', allow_same ? '1' : '0');
    formdata.append('hide_online', hide_online ? '1' : '0');
    formdata.append('availability', JSON.stringify(availability));
    return this.http.post(this.baseUrl + 'modifierscategory_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  duplicateModifierCategories(id) {
    const formdata = new FormData();
    formdata.append('id', id);
  
    return this.http.post(this.baseUrl + 'modifierscategory_duplicate', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getModifierCategories() {
    return this.http.get(this.baseUrl + 'modifierscategoryp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getModifierCategoriesPaginated(payload: {
    per_page?: number;
    page?: number;
    search?: string;
  }): Observable<any> {
    return this.http.post(this.baseUrl + 'modifierscategory_search_paginated', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  editModifierCategories(id: any, name: any, is_mandatory: any, min_modifier: any, max_modifier: any, allow_same: any, hide_online: any, availability=null) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    formdata.append('is_mandatory', is_mandatory ? '1' : '0');
    formdata.append('min_modifier', min_modifier);
    formdata.append('max_modifier', max_modifier);
    formdata.append('allow_same', allow_same ? '1' : '0');
    formdata.append('hide_online', hide_online ? '1' : '0');
    formdata.append('availability', JSON.stringify(availability));
    return this.http.post(this.baseUrl + 'modifierscategory_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteModifierCategory(id: any) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'modifierscategory_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteModifierCategoryByProductId(id: any) {
    const formdata = new FormData();
    formdata.append('category_id', id);
    return this.http.post(this.baseUrl + 'modifiers_delete_by_category', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }




  //////////////// Modifiers

  createModifiers(name: any, price, cost, plus, category_id, hide_online, preselect, color, is_alert, alert_text, image = null) {
    console.log(`Check point'`, image);

    const formdata = new FormData();
    formdata.append('cat_id', category_id);
    formdata.append('name', name);
    formdata.append('price', price);
    formdata.append('cost', cost);
    formdata.append('plus', plus ? '1' : '0');
    formdata.append('hide_online', hide_online ? '1' : '0');
    formdata.append('preselect', preselect ? '1' : '0');
    formdata.append('color', color);
    formdata.append('is_alert', is_alert ? '1' : '0');
    formdata.append('alert_text', alert_text);
    if (image) {
      formdata.append('image_id', image);
    }
    return this.http.post(this.baseUrl + 'modifiers_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }




  addModifier(values) {
    const formdata = new FormData();
    formdata.append('cat_id', values.modifiers_category_id);
    formdata.append('name', values.modifiers_name);
    formdata.append('price', values.price);
    formdata.append('cost', values.cost);
    formdata.append('plus', values.plus ? '1' : '0');
    formdata.append('hide_online', values.hide_online ? '1' : '0');
    formdata.append('preselect', values.preselect ? '1' : '0');
    formdata.append('color', values.color);
    formdata.append('is_alert', values.is_alert ? '1' : '0');
    formdata.append('alert_text', values.alert_text);
    if (values.image_id) {
      formdata.append('image_id', values.image_id);
    }
    formdata.append('availability', JSON.stringify(values.availability));
    if (values.pricetags) {
      const modifierPricetags = (values.pricetags as any[])
        .filter((p) => p && p.id != null && p.price !== '' && p.price != null)
        .map((p) => ({
          id: p.id,
          price: p.price,
        }));
      formdata.append('pricetags', JSON.stringify(modifierPricetags));
    }
    return this.http.post(this.baseUrl + 'modifiers_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getModifiers() {
    return this.http.get(this.baseUrl + 'modifiersp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getModifiersPaginated(payload: {
    per_page?: number;
    page?: number;
    search?: string;
    category_id?: number | string;
  }): Observable<any> {
    return this.http.post(this.baseUrl + 'modifiers_search_paginated', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editModifiers(id: any, name: any, price, cost, plus, cat_id, hide_online, color, preselect, is_alert, alert_text, image_id = null,) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    formdata.append('cat_id', cat_id);
    formdata.append('price', price);
    formdata.append('cost', cost);
    formdata.append('plus', plus ? '1' : '0');
    formdata.append('hide_online', hide_online ? '1' : '0');
    formdata.append('color', color);
    formdata.append('preselect', preselect ? '1' : '0');

    if (image_id != null) {
      formdata.append('image_id', image_id);
    }
    formdata.append('is_alert', is_alert ? '1' : '0');
    formdata.append('alert_text', alert_text);
    return this.http.post(this.baseUrl + 'modifiers_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateModifiers(values) {
    const formdata = new FormData();
    formdata.append('id', values.id);
    formdata.append('cat_id', values.modifiers_category_id);
    formdata.append('name', values.modifiers_name);
    formdata.append('price', values.price);
    formdata.append('cost', values.cost);
    formdata.append('plus', values.plus ? '1' : '0');
    formdata.append('hide_online', values.hide_online ? '1' : '0');
    formdata.append('preselect', values.preselect ? '1' : '0');
    formdata.append('color', values.color);
    formdata.append('is_alert', values.is_alert ? '1' : '0');
    formdata.append('alert_text', values.alert_text);
    if (values.image_id) {
      formdata.append('image_id', values.image_id);
    }
    formdata.append('availability', JSON.stringify(values.availability));
    if (values.pricetags) {
      const modifierPricetags = (values.pricetags as any[])
        .filter((p) => p && p.id != null && p.price !== '' && p.price != null)
        .map((p) => ({
          id: p.id,
          price: p.price,
        }));
      formdata.append('pricetags', JSON.stringify(modifierPricetags));
    }

    return this.http.post(this.baseUrl + 'modifiers_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteModifier(id: any) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'modifiers_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  createUpdateModifierToProductsMapping(data: any) {
    const formdata = new FormData();
    formdata.append('data', data);
    return this.http.post(this.baseUrl + 'modifier_map_to_products', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  createUpdateModifierCatToProductsMapping(data: any) {
    const formdata = new FormData();
    formdata.append('data', data);
    return this.http.post(this.baseUrl + 'add_prod_modcat_mapping_bulk', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getModifierToProductsMapping(id: any) {
    return this.http.get(this.baseUrl + 'modifier_map_to_products?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /////////////////// Price Tags

  createPricetags(name) {
    const formdata = new FormData();
    formdata.append('name', name);
    return this.http.post(this.baseUrl + 'pricetag_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  createPricetag(name,automatic,availability) {
    const formdata = new FormData();
    formdata.append('name', name);
    formdata.append('automatic', automatic);
    formdata.append('availability', JSON.stringify(availability));
    return this.http.post(this.baseUrl + 'pricetag_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editPricetags(id, name) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    return this.http.post(this.baseUrl + 'pricetag_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  editPricetag(id, name,automatic,availability) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    formdata.append('automatic', automatic);
    formdata.append('availability', JSON.stringify(availability));
    return this.http.post(this.baseUrl + 'pricetag_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getPricetags() {
    return this.http.get(this.baseUrl + 'pricetagp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  // Grid Buttons
  getGridButtons() {
    return this.http.get(this.baseUrl + 'grid-buttons', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createGridButton(payload: any) {
    return this.http.post(this.baseUrl + 'grid-buttons', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
        'Content-Type': 'application/json',
      }),
    });
  }

  updateGridButton(id: string, payload: any) {
    return this.http.put(this.baseUrl + 'grid-buttons/' + id, payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
        'Content-Type': 'application/json',
      }),
    });
  }

  deleteGridButton(id: string) {
    return this.http.delete(this.baseUrl + 'grid-buttons/' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateGridButtonOrder(payload: Array<{ id: string; position: number }>) {
    return this.http.post(
      this.baseUrl + 'grid-buttons/bulk/update-positions',
      { buttons: payload },
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
          'Content-Type': 'application/json',
        }),
      },
    );
  }

  deletepricetag(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'pricetag_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /////////////////// Table Groups

  createTableGroups(name, image_background) {
    const formdata = new FormData();
    formdata.append('name', name);
    if(image_background){
    formdata.append('image_background', image_background);
    }
    return this.http.post(this.baseUrl + 'tablegroup_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editTableGroups(id, name, image_background) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    formdata.append('image_background', image_background);
    return this.http.post(this.baseUrl + 'tablegroup_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getTableGroups() {
    return this.http.get(this.baseUrl + 'tablegroupp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteTableGroup(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'tablegroup_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  //courses

  getCourses() {
    return this.http.get(this.baseUrl + 'courses', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  createCourses(name,position,pricetag) {
    const formdata = new FormData();
    formdata.append('name', name);
    formdata.append('position', position);
    if (pricetag !== null && pricetag !== '') {
      formdata.append('price_tag', pricetag);
    }
    
    return this.http.post(this.baseUrl + 'courses', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateCourses(id,name,position,pricetag) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    formdata.append('position', position);
    
    if (pricetag !== null && pricetag !== '') {

      formdata.append('price_tag', pricetag);
    }
  
    return this.http.post(this.baseUrl + 'course_update', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  

  deleteCourse(id) {
   
    return this.http.delete(this.baseUrl + 'courses/' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /////////////////// VoidReasons

  
  createVoidReasons(name) {
    const formdata = new FormData();
    formdata.append('name', name);
    return this.http.post(this.baseUrl + 'voidreason_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editVoidReasons(id, name) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    return this.http.post(this.baseUrl + 'voidreason_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getVoidReasons() {
    return this.http.get(this.baseUrl + 'voidreasonp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteVoidReason(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'voidreason_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /////////////////// Discounts

  createDiscounts(payload) {

    return this.http.post(this.baseUrl + 'discount_add', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editDiscounts(payload) {

    return this.http.post(this.baseUrl + 'discount_edit', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getDiscounts() {
    return this.http.get(this.baseUrl + 'discountp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteDiscount(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'discount_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /////////////////// Paymenttypes

  createPaymentTypes(name, online, table, self, pos, del, pick,surcharge) {
    const formdata = new FormData();
    formdata.append('name', name);
    formdata.append('is_online', online == true || online == 1 ? '1' : '0');
    formdata.append('is_table', table == true || table == 1 ? '1' : '0');
    formdata.append('is_selfkiosk', self == true || self == 1 ? '1' : '0');
    formdata.append('is_pos', pos == true || pos == 1 ? '1' : '0');
    formdata.append('is_online_delivery', del == true || del == 1 ? '1' : '0');
    formdata.append('is_online_pickup', pick == true || pick == 1 ? '1' : '0');
    formdata.append('surcharge',surcharge ? surcharge : '0');
    return this.http.post(this.baseUrl + 'paymenttype_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editPaymentTypes(id, name, online, table, self, pos, del, pick,surcharge) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    formdata.append('is_online', online == true || online == 1 ? '1' : '0');
    formdata.append('is_table', table == true || table == 1 ? '1' : '0');
    formdata.append('is_selfkiosk', self == true || self == 1 ? '1' : '0');
    formdata.append('is_pos', pos == true || pos == 1 ? '1' : '0');
    formdata.append('is_online_delivery', del == true || del == 1 ? '1' : '0');
    formdata.append('is_online_pickup', pick == true || pick == 1 ? '1' : '0');
    formdata.append('surcharge',surcharge ? surcharge : '0');
    return this.http.post(this.baseUrl + 'paymenttype_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getPaymentTypes() {
    return this.http.get(this.baseUrl + 'paymenttypep', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deletePaymentType(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'paymenttype_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /////////////////// ModifierMapping

  createModifierMappings(productCat, modifierCat) {

    const formdata = new FormData();
    formdata.append('category_id', productCat);
    formdata.append('modifierscategory_id', modifierCat);
    formdata.append('forced_count', '1');
    return this.http.post(
      this.baseUrl + 'productmodifiersmapping_add',
      formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  editModifierMappings(id, productCat, modifierCat) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('category_id', productCat);
    formdata.append('modifierscategory_id', modifierCat);
    formdata.append('forced_count', '1');
    return this.http.post(
      this.baseUrl + 'productmodifiersmapping_edit',
      formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getModifierMappings() {
    return this.http.get(this.baseUrl + 'productmodifiersmappingp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getModifierMappingsAll() {
    return this.http.get(this.baseUrl + 'productmodifiersmapping_all', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getModifiersMappedToProductByAll(id) {
    return this.http.get(this.baseUrl + 'modifiers_of_product?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteModifierMapping(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(
      this.baseUrl + 'productmodifiersmapping_delete',
      formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  /////////////////// Tables

  createTables(name, seat, alert, group,position) {
    const formdata = new FormData();
    formdata.append('name', name);
    formdata.append('total_seat_count', seat);
    formdata.append('alerttime', alert);
    formdata.append('tablegroup_id', group);
    formdata.append('position', position);
    return this.http.post(this.baseUrl + 'table_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editTables(id, name, seat, alert, group) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    formdata.append('total_seat_count', seat);
    formdata.append('alerttime', alert);
    formdata.append('tablegroup_id', group);
    return this.http.post(this.baseUrl + 'table_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getTables() {
    return this.http.get(this.baseUrl + 'tablep', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteTables(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'table_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  //////////////////// Staff

  getStaffs() {
    return this.http.get(this.baseUrl + 'staffp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getStaffById(id) {
    return this.http.get(this.baseUrl + 'staff_by_id?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  createStaff(name: any, pin: any, role: any) {
    const formdata = new FormData();
    formdata.append('name', name);
    formdata.append('pin', pin);
    formdata.append('role_id', role);
    return this.http.post(this.baseUrl + 'staff_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editStaff(id: any, name: any, pin: any, role: any) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    formdata.append('pin', pin);
    formdata.append('role_id', role);
    return this.http.post(this.baseUrl + 'staff_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteStaff(id: any) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'staff_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  sendPushNotification(data: any) {
    const formdata = new FormData();
    formdata.append('title', data.title);
    formdata.append('body', data.body);
    return this.http.post(this.baseUrl + 'push_notification_to_app', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  //////////////// Products

  createProducts(add: any, image: any) {
    console.log(add.size);
    console.log(JSON.stringify(add.size));

    let expdate = moment(add.expiry_date).format()

    const formdata = new FormData();
    formdata.append('category_id', add.category_id);
    formdata.append('subcategory_id', add.subcategory_id);
    formdata.append('name', add.name);
    formdata.append('description', add.description);
    formdata.append('code', add.code);
    formdata.append('sale_price', add.sale_price);
    formdata.append('availability', JSON.stringify(add.availability));
    formdata.append('color', add.color);
    formdata.append('vat_id', '0');
    formdata.append('askprice', add.askprice ? '1' : '0');
    formdata.append('askqty', add.askqty ? '1' : '0');
    formdata.append('kitchennote', add.kitchennote ? '1' : '0');
    formdata.append('weight', add.weight ? '1' : '0');
    formdata.append('tax_in_cost', add.tax_in_cost ? '1' : '0');
    formdata.append('discountable', add.discountable ? '1' : '0');
    formdata.append('is_inventory', add.is_inventory ? '1' : '0');
    formdata.append('stock', add.stock);
    formdata.append('stock_unit', add.stock_unit);
    formdata.append('cost', add.cost);
    formdata.append('pricetag', JSON.stringify(add.pricetag));
    formdata.append('size', JSON.stringify(add.size));
    formdata.append('banner', add.banner);
    formdata.append('show_only', add.showOnly ? '1' : '0');
    formdata.append('is_inactive', add.is_inactive ? '1' : '0');
    formdata.append('inactive_reason', add.inactive_reason);
    formdata.append('special_instruction_hide', add.special_instruction_hide ? '1' : '0');
    formdata.append('is_hidden', add.is_hidden ? '1' : '0');
    formdata.append('expiry_date', expdate);
    formdata.append('short_name', add.short_name);
    formdata.append('table_price', add.table_price != null ? add.table_price :add.sale_price);
    formdata.append('hidden_online_table_menu', add.hidden_online_table_menu ? '1' : '0');
    formdata.append('is_loyalty_product', add.is_loyalty_product ? '1' : '0');
    formdata.append('is_countable_product', add.is_countable_product ? '1' : '0');
    formdata.append('is_t', add.is_timed_product ? '1' : '0');
    formdata.append('ignore_receipt', add.ignore_receipt ? '1' : '0');
    formdata.append('banner_price', add.banner_price );
    formdata.append('sales_category_id', add.sales_category_id );
    formdata.append('topup_meta', add.topup_meta ? add.topup_meta: null);
    formdata.append('topup', add.topup ? '1' : '0');
    formdata.append('av', add.av ? '1' : '0');

    if (image != '') {
      formdata.append('photo', image);
    }
    return this.http.post(this.baseUrl + 'product_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProducts() {
    return this.http.get(this.baseUrl + 'productpq', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getProductNames(data) {
    return this.http.post(this.baseUrl + 'product_names',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getCategoryNames(data) {
    return this.http.post(this.baseUrl + 'category_names',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getAllProducts() {
    return this.http.get(this.baseUrl + 'getAllProduct', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProductById(productId: number) {
    return this.http.get(this.baseUrl + `product_single?product_id=${productId}`, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProductsPaginated(payload: {
    per_page?: number;
    page?: number;
    search?: string;
    category_id?: number | string;
    subcategory_id?: number | string;
  }): Observable<any> {
    return this.http.post(this.baseUrl + 'products_search_paginated', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getProductsDatasPaginated(payload: {
    per_page?: number;
    page?: number;
    search?: string;
    category_id?: number | string;
    subcategory_id?: number | string;
  }): Observable<any> {
    return this.http.post(this.baseUrl + 'products_data_paginated', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProductCostByInventories(id) {
    return this.http.get(this.baseUrl + 'get_product_cost_by_inventories?product_id='+id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  syncDataChanges() {
    return this.http.get(this.baseUrl + 'sync_data_changes', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  productDataSync() {
    return this.http.get(this.baseUrl + 'productDataRefresh', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateProductBulk(payload) {
    return this.http.post(this.baseUrl + 'custom/products/update',payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProductsBysub(cat, sub) {
    return this.http.get(this.baseUrl + 'get_product_sub?category_id=' + cat + '&subcategory_id=' + sub, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getSubbyCat(cat) {
    return this.http.get(this.baseUrl + 'get_sub_by_cat?category_id=' + cat, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  editProducts(id: any, add: any, image: any) {


    let expdate = moment(add.expiry_date).format()
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('category_id', add.category_id);
    formdata.append('subcategory_id', add.subcategory_id);
    formdata.append('availability', JSON.stringify(add.availability));
    formdata.append('description', add.description);
    formdata.append('name', add.name);
    formdata.append('code', add.code);
    formdata.append('sale_price', add.sale_price);
    formdata.append('color', add.color);
    formdata.append('vat_id', '0');
    formdata.append('askprice', add.askprice ? '1' : '0');
    formdata.append('askqty', add.askqty ? '1' : '0');
    formdata.append('kitchennote', add.kitchennote ? '1' : '0');
    formdata.append('weight', add.weight ? '1' : '0');
    formdata.append('tax_in_cost', add.tax_in_cost ? '1' : '0');
    formdata.append('discountable', add.discountable ? '1' : '0');
    formdata.append('is_inventory', add.is_inventory ? '1' : '0');
    formdata.append('stock', add.stock);
    formdata.append('stock_unit', add.stock_unit);
    formdata.append('cost', add.cost);
    formdata.append('pricetag', JSON.stringify(add.pricetag));
    formdata.append('size', JSON.stringify(add.size));
    formdata.append('banner', add.banner);
    formdata.append('show_only', add.showOnly ? '1' : '0');
    formdata.append('is_inactive', add.is_inactive ? '1' : '0');
    formdata.append('inactive_reason', add.inactive_reason);
    formdata.append('special_instruction_hide', add.special_instruction_hide ? '1' : '0');
    formdata.append('is_hidden', add.is_hidden ? '1' : '0');
    formdata.append('expiry_date', expdate);
    formdata.append('short_name', add.short_name);
    formdata.append('table_price', add.table_price != null ? add.table_price :add.sale_price);
    formdata.append('hidden_online_table_menu', add.hidden_online_table_menu ? '1' : '0');
    formdata.append('is_loyalty_product', add.is_loyalty_product ? '1' : '0');
    formdata.append('is_countable_product', add.is_countable_product ? '1' : '0');
    formdata.append('is_t', add.is_timed_product ? '1' : '0');
    formdata.append('ignore_receipt', add.ignore_receipt ? '1' : '0');
    formdata.append('banner_price', add.banner_price);
    formdata.append('av', add.av ? '1' : '0');
    if (image != null) {
      formdata.append('image', image);
    }
    return this.http.post(this.baseUrl + 'product_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteProduct(id: any) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'product_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  hideProduct(id: any, hidden: any) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('is_hidden', hidden);
    return this.http.post(this.baseUrl + 'product_hidden', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  hideProductSize(id: any, hidden: any) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('is_hidden', hidden);
    return this.http.post(this.baseUrl + 'productsize_hide_menu', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  inactiveProduct(id: any, inactive: any) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('is_inactive', inactive);
    return this.http.post(this.baseUrl + 'product_inactive', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getDeliveryIntegration() {
    return this.http.get(this.baseUrl + 'get_delivery_integration', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getProductStock(id:any) {
    return this.http.get(this.baseUrl + 'get_product_stock?id='+id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateProductStock(data:any) {
    return this.http.post(this.baseUrl + 'update_product_stock',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateProductTopup(data:any) {
    return this.http.post(this.baseUrl + 'update_product_topup', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createDeliveryIntegration(data:any) {
    return this.http.post(this.baseUrl + 'create_delivery_integration', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  
  deleteDeliveryIntegration(data:any) {
    return this.http.post(this.baseUrl + 'delete_delivery_integration', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  productSizeDelete(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'productsize_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  productSizeEdit(data: any) {
    const formdata = new FormData();
    formdata.append('id', data?.id);
    formdata.append('size_name', data?.size_name);
    formdata.append('size_amount', data?.size_amount);
    formdata.append('multiplier', data?.multiplier);
    formdata.append('position', data?.position);
    let availablemenu = JSON.stringify(data?.available_menu);
    formdata.append('available_menu', availablemenu);
    return this.http.post(this.baseUrl + 'productsize_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  productImageRemove(id) {

    return this.http.get(this.baseUrl + 'product_image_null?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }





  addProductAvailability(id, data) {
    const formdata = new FormData();
    formdata.append('product_id', id);
    formdata.append('day', data.day);
    formdata.append('is_active', data.active ? '1' : '0')
    formdata.append('start', data.start);
    formdata.append('end', data.end);

    return this.http.post(this.baseUrl + 'add_prod_availability', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteProductAvailability(id) {
    const formdata = new FormData();
    formdata.append('id', id);



    return this.http.post(this.baseUrl + 'delete_prod_availability', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProductAvailability(id) {

    return this.http.get(this.baseUrl + 'get_prod_availability?product_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }




  categoryImageRemove(id) {

    return this.http.get(this.baseUrl + 'category_image_null?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }




  getProductTags(id) {
   // console.log("id", id);

    return this.http.get(this.baseUrl + 'product_tags_get?product_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });

  }

  addProductTags(name, id) {
    const formdata = new FormData();
    formdata.append('product_id', id);
    formdata.append('tag', name);
    console.log(formdata);

    return this.http.post(this.baseUrl + 'product_tags_import', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });

  }

  deleteProductTags(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'product_tags_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });

  }

  /** Menu tag mapping: get tags mapped to a product (edit mode). */
  getMappedProductMenuTags(productId: string | number) {
    const formdata = new FormData();
    formdata.append('product_id', String(productId));
    return this.http.post(this.baseUrl + 'get_mapped_product_menu_tags', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Attach a menu tag to a product. */
  attachProductToMenuTag(productId: string | number, menuTagId: number) {
    const formdata = new FormData();
    formdata.append('product_id', String(productId));
    formdata.append('tag_id', String(menuTagId));
    return this.http.post(this.baseUrl + 'attach_product_to_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Detach a menu tag from a product. */
  detachProductFromMenuTag(productId: string | number, menuTagId: number) {
    const formdata = new FormData();
    formdata.append('product_id', String(productId));
    formdata.append('tag_id', String(menuTagId));
    return this.http.post(this.baseUrl + 'detach_product_from_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Category menu tag mapping: get tags mapped to a category. */
  getMappedCategoryMenuTags(categoryId: string | number) {
    const formdata = new FormData();
    formdata.append('category_id', String(categoryId));
    return this.http.post(this.baseUrl + 'get_mapped_category_menu_tags', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Attach a menu tag to a category. */
  attachCategoryToMenuTag(categoryId: string | number, menuTagId: number) {
    const formdata = new FormData();
    formdata.append('category_id', String(categoryId));
    formdata.append('tag_id', String(menuTagId));
    return this.http.post(this.baseUrl + 'attach_category_to_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Detach a menu tag from a category. */
  detachCategoryFromMenuTag(categoryId: string | number, menuTagId: number) {
    const formdata = new FormData();
    formdata.append('category_id', String(categoryId));
    formdata.append('tag_id', String(menuTagId));
    return this.http.post(this.baseUrl + 'detach_category_from_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Modifier menu tag mapping: get tags mapped to a modifier. */
  getMappedModifierMenuTags(modifierId: string | number) {
    const formdata = new FormData();
    formdata.append('modifier_id', String(modifierId));
    return this.http.post(this.baseUrl + 'get_mapped_modifier_menu_tags', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Attach a menu tag to a modifier. */
  attachModifierToMenuTag(modifierId: string | number, menuTagId: number) {
    const formdata = new FormData();
    formdata.append('modifier_id', String(modifierId));
    formdata.append('tag_id', String(menuTagId));
    return this.http.post(this.baseUrl + 'attach_modifier_to_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Detach a menu tag from a modifier. */
  detachModifierFromMenuTag(modifierId: string | number, menuTagId: number) {
    const formdata = new FormData();
    formdata.append('modifier_id', String(modifierId));
    formdata.append('tag_id', String(menuTagId));
    return this.http.post(this.baseUrl + 'detach_modifier_from_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Modifier category menu tag mapping: get tags mapped to a modifier category. */
  getMappedModifierCategoryMenuTags(modifierCategoryId: string | number) {
    const formdata = new FormData();
    formdata.append('modifier_cat_id', String(modifierCategoryId));
    return this.http.post(this.baseUrl + 'get_mapped_modifier_category_menu_tags', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Attach a menu tag to a modifier category. */
  attachModifierCategoryToMenuTag(modifierCategoryId: string | number, menuTagId: number) {
    const formdata = new FormData();
    formdata.append('modifier_cat_id', String(modifierCategoryId));
    formdata.append('tag_id', String(menuTagId));
    return this.http.post(this.baseUrl + 'attach_modifier_category_to_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Detach a menu tag from a modifier category. */
  detachModifierCategoryFromMenuTag(modifierCategoryId: string | number, menuTagId: number) {
    const formdata = new FormData();
    formdata.append('modifier_cat_id', String(modifierCategoryId));
    formdata.append('tag_id', String(menuTagId));
    return this.http.post(this.baseUrl + 'detach_modifier_category_from_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Get categories mapped to a menu tag. */
  /** Get categories mapped to a menu tag (paginated). */
  getCategoriesMappedToMenuTag(tagId: number | string, params?: { page?: number; per_page?: number }) {
    const formdata = new FormData();
    formdata.append('tag_id', String(tagId));
    if (params?.page != null) formdata.append('page', String(params.page));
    if (params?.per_page != null) formdata.append('per_page', String(params.per_page));
    return this.http.post(this.baseUrl + 'get_categories_mapped_to_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Get products mapped to a menu tag (paginated). */
  getProductsMappedToMenuTag(tagId: number | string, params?: { page?: number; per_page?: number }) {
    const formdata = new FormData();
    formdata.append('tag_id', String(tagId));
    if (params?.page != null) formdata.append('page', String(params.page));
    if (params?.per_page != null) formdata.append('per_page', String(params.per_page));
    return this.http.post(this.baseUrl + 'get_products_mapped_to_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Get modifier categories mapped to a menu tag (paginated). */
  getModifierCategoriesMappedToMenuTag(tagId: number | string, params?: { page?: number; per_page?: number }) {
    const formdata = new FormData();
    formdata.append('tag_id', String(tagId));
    if (params?.page != null) formdata.append('page', String(params.page));
    if (params?.per_page != null) formdata.append('per_page', String(params.per_page));
    return this.http.post(this.baseUrl + 'get_modifier_categories_mapped_to_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /** Get modifiers mapped to a menu tag (paginated). */
  getModifiersMappedToMenuTag(tagId: number | string, params?: { page?: number; per_page?: number }) {
    const formdata = new FormData();
    formdata.append('tag_id', String(tagId));
    if (params?.page != null) formdata.append('page', String(params.page));
    if (params?.per_page != null) formdata.append('per_page', String(params.per_page));
    return this.http.post(this.baseUrl + 'get_modifiers_mapped_to_menu_tag', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  ///////////////////////// Roles

  getroles() {
    return this.http.get(this.baseUrl + 'rolep', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createRoles(add: any,admin_permissions:any) {
    const formdata = new FormData();
    formdata.append('name', add.name);
    formdata.append('permissions', JSON.stringify(add.permissions));
    formdata.append('admin_permissions', JSON.stringify(admin_permissions));
    return this.http.post(this.baseUrl + 'role_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editRoles(id: any, add: any, admin_permissions) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', add.name);
    formdata.append('permissions', JSON.stringify(add.permissions));
    formdata.append('admin_permissions', JSON.stringify(admin_permissions));
    return this.http.post(this.baseUrl + 'role_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteRole(id: any) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'role_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /////////////////// Inventory

  createInventory(name, value, percent) {
    const formdata = new FormData();
    formdata.append('name', name);
    formdata.append('percentage', percent ? '1' : '0');
    formdata.append('value', value);
    return this.http.post(this.baseUrl + 'discount_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  editInventory(id, name, value, percent) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('name', name);
    formdata.append('percentage', percent ? '1' : '0');
    formdata.append('value', value);
    return this.http.post(this.baseUrl + 'discount_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getInventory() {
    return this.http.get(this.baseUrl + 'inventoryp', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getInventorydeletedIncluded() {
    return this.http.get(this.baseUrl + 'inventoryp_deleted_in', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  createProductInventory(data) {
    return this.http.post(this.baseUrl + 'create_product_inventory', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createProductInventoryBulkByIds(payload: {
    product_ids?: number[];
    category_ids?: number[];
  }): Observable<any> {
    return this.http.post(this.baseUrl + 'create_product_inventory_bulk_by_ids', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createProductInventoryBulkAsync(payload: {
    product_ids?: number[];
    category_ids?: number[];
    batch_size?: number;
  }): Observable<any> {
    return this.http.post(this.baseUrl + 'create_product_inventory_bulk_async', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getBulkInventoryJobStatus(jobId: string): Observable<any> {
    return this.http.get(this.baseUrl + 'bulk_inventory_job_status/' + jobId, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUserBulkInventoryJobs(): Observable<any> {
    return this.http.get(this.baseUrl + 'user_bulk_inventory_jobs', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getProductInventory(id: any) {
    return this.http.get(this.baseUrl + 'get_product_inventory?product_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getInventoryOrderingLevel() {
    return this.http.get(this.baseUrl + 'get_ordering_level', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateInventoryOrderingLevel(data:any) {
    return this.http.post(this.baseUrl + 'update_ordering_level',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getProductInventoryAll() {
    return this.http.get(this.baseUrl + 'get_product_inventory_all', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteInventory(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'discount_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  /// inventory category

  getInventoryCategories() {
    return this.http.get(this.baseUrl + 'inventory_category_get', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getInventoriesListing(params: {
    search?: string;
    category_id?: number | string;
    stock_management_type?: string;
    low_stock?: boolean;
    zero_stock?: boolean;
    per_page?: number;
    page?: number;
    product_category?: number | string;
  }): Observable<any> {
    let queryParams = new HttpParams();
    if (params.search) queryParams = queryParams.set('search', params.search);
    if (params.category_id) queryParams = queryParams.set('category_id', params.category_id.toString());
    if (params.stock_management_type) queryParams = queryParams.set('stock_management_type', params.stock_management_type);
    if (params.low_stock !== undefined) queryParams = queryParams.set('low_stock', params.low_stock.toString());
    if (params.zero_stock !== undefined) queryParams = queryParams.set('zero_stock', params.zero_stock.toString());
    if (params.per_page) queryParams = queryParams.set('per_page', params.per_page.toString());
    if (params.page) queryParams = queryParams.set('page', params.page.toString());
    if (params.product_category) queryParams = queryParams.set('product_category', params.product_category.toString());

    return this.http.get(this.baseUrl + 'inventories_listing', {
      params: queryParams,
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getInventoryCategoriesByInventoryId(id) {
    let data = {
      id: id
    };
    return this.http.post(this.baseUrl + 'inventory_category_get_by_id', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createInventoryCategory(data: any) {
    return this.http.post(this.baseUrl + 'inventory_category_add', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateInventoryCategory(data: any) {
    return this.http.post(this.baseUrl + 'inventory_category_update', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  deleteInventoryCategory(data: any) {
    return this.http.post(this.baseUrl + 'inventory_category_delete', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  // stock 

  
  inventoryStockHistory(data){
    return this.http.post(this.baseUrl + 'inventory_stock_data_on_date_time',data,{
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


// door dash delivery

  

  listDoordashDeliveryStore(){
    return this.http.get(this.baseUrl + 'list_stores_doordash',{
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createDoordashDeliveryStore(data){
    return this.http.post(this.baseUrl + 'create_store_doordash',data,{
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  createDoordashDeliveryBusiness(data){
    return this.http.post(this.baseUrl + 'create_business_doordash',data,{
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateDoordashDeliveryStore(data){
    return this.http.post(this.baseUrl + 'update_store_doordash',data,{
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  ///////////////////////////////////export xlsx

  fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  fileExtension = '.xlsx';

  generateExcel(jsonData: any[], fileName: string): void {
    //console.log(jsonData);
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    const wb: XLSX.WorkBook = { Sheets: { 'data': ws }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    this.saveExcelFile(excelBuffer, fileName);
  }
  private saveExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: this.fileType });
    FileSaver.saveAs(data, fileName + this.fileExtension);
  }


  ////////////////////////// sort

  //category sort

  getCategoriesSort() {
    return this.http.get(this.baseUrl + 'category_sort', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  saveCategoriesSort(data) {
    return this.http.post(this.baseUrl + 'category_sort', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  savePosTableSort(data) {
    return this.http.post(this.baseUrl + 'table_sort_save', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  saveProductSubSort(data) {
    return this.http.post(this.baseUrl + 'productsort', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getSubCategoriesSort() {
    return this.http.get(this.baseUrl + 'subcategory_sort', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  saveSubCategoriesSort(data) {
    return this.http.post(this.baseUrl + 'subcategory_sort', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  //modifier category sort


  getModCategoriesSort() {
    return this.http.get(this.baseUrl + 'modifierscategory_sort', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  saveModCategoriesSort(data) {
    return this.http.post(this.baseUrl + 'modifierscategory_sort', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  //modifiers sort

  getModByCat(id) {
    return this.http.get(this.baseUrl + 'getModyfiersByCategory?mcatid=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  modifiersSort(data) {
    return this.http.post(this.baseUrl + 'modifiers_sort', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }






  //////////////////inventory

  getAllInventory() {
    return this.http.get(this.baseUrl + 'inventory_all', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }
  getVendorManagedInventory() {
    return this.http.get(this.baseUrl + 'inventory_vendor_managed', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }
  getAllInventoryWithMaster() {
    return this.http.get(this.baseUrl + 'inventory_all_with_master', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  getMappedStock(product_id) {

    const formdata = new FormData();
    formdata.append('product_id', product_id);

    return this.http.post(this.baseUrl + 'get_mapped_stock', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  inventoryEdit(id, data, pro_id, is_pro, lastupdate_flag) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('alert', data['alert']);
    formdata.append('code', data['code']);
    formdata.append('pack_count', data['pack']);
    formdata.append('name', data['productname']);
    formdata.append('stock', data['stock']);
    formdata.append('ordering_level', data['ordering_level'] ? data['ordering_level'] : '');
    formdata.append('cat_id', data['cat_id'] ? data['cat_id'] : '');
    formdata.append('stock_type', data['stocktype']);
    formdata.append('stock_unit', data['stockunit']);
    formdata.append('cost', data['cost']);
    formdata.append('is_product', is_pro);
    formdata.append('product_id', pro_id);
    formdata.append('stock_last_update', lastupdate_flag);
    formdata.append('is_daily', data['daily'] ? '1' : '0');
    formdata.append('is_weekly', data['weekly'] ? '1' : '0');
    formdata.append('is_monthly', data['monthly'] ? '1' : '0');
    formdata.append('is_fortnightly', data['fortnightly'] ? '1' : '0');
    formdata.append('stock_management_type', data['stock_management_type']);
    formdata.append('cost_update_strategy', data['cost_update_strategy']);
    formdata.append('barcode', data['barcode']);
    formdata.append('sku', data['sku']);
    formdata.append('tax_in_cost', data['tax_in_cost'] ? '1' : '0');
    console.log("check last ", lastupdate_flag);
    return this.http.post(this.baseUrl + 'inventory_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  inventoryDelete(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(this.baseUrl + 'inventory_delete', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  
  





  addInventory(name, code, stock, stock_unit, cost, stock_type, pack_count, alert, daily, weekly, monthly, fortnightly, ordering_level, cat_id,stock_management_type,cost_update_strategy,expiry_date,barcode,sku,tax_in_cost) {
    const formdata = new FormData();
    formdata.append('name', name);
    formdata.append('code', code);
    formdata.append('stock', stock);
    formdata.append('stock_unit', stock_unit);
    formdata.append('cost', cost);
    formdata.append('stock_type', stock_type);
    formdata.append('pack_count', pack_count);
    formdata.append('is_product', '0');
    formdata.append('product_id', '0');
    formdata.append('alert', alert);
    formdata.append('is_daily', daily ? '1' : '0');
    formdata.append('is_weekly', weekly ? '1' : '0');
    formdata.append('is_monthly', monthly ? '1' : '0');
    formdata.append('is_fortnightly', fortnightly ? '1' : '0');
    formdata.append('ordering_level', ordering_level);
    formdata.append('cat_id', cat_id);
    formdata.append('stock_management_type', stock_management_type);
    formdata.append('cost_update_strategy', cost_update_strategy);
    formdata.append('barcode', barcode);
    formdata.append('sku', sku);
    formdata.append('tax_in_cost', tax_in_cost ? '1' : '0');
    if(expiry_date){
      formdata.append('expiry_date', expiry_date);
    }
    return this.http.post(this.baseUrl + 'add_inventoryItem', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });

  }

  productInventoryMapping(product_id, inventory_id, mapping_weight,product_size_id=null) {
    const formdata = new FormData();
    formdata.append('product_id', product_id);
    formdata.append('inventory_id', inventory_id);
    if (product_size_id !== null) { // Only append if product_size_id is not null
      formdata.append('product_size_id', product_size_id);
  }
    formdata.append('mapping_weight', mapping_weight);
    return this.http.post(this.baseUrl + 'product_inventory_mapping', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  
  productInventoryMappingEdit(mapping_id,product_id, inventory_id, mapping_weight) {
    const formdata = new FormData();
    formdata.append('id', mapping_id);
    formdata.append('product_id', product_id);
    formdata.append('inventory_id', inventory_id);
    formdata.append('mapping_weight', mapping_weight);
    return this.http.post(this.baseUrl + 'product_inventory_mapping_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  productInventoryMappingUpdate(mapping_id,product_id, inventory_id, mapping_weight,product_size_id=null) {
    const formdata = new FormData();
    formdata.append('id', mapping_id);
    formdata.append('product_id', product_id);
    formdata.append('inventory_id', inventory_id);
    if (product_size_id !== null) { // Only append if product_size_id is not null
      formdata.append('product_size_id', product_size_id);
  }
    formdata.append('mapping_weight', mapping_weight);
    return this.http.post(this.baseUrl + 'update_mapped_stock', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  modifierInventoryMapping(product_id, inventory_id, mapping_weight) {
    const formdata = new FormData();
    formdata.append('modifier_id', product_id);
    formdata.append('inventory_id', inventory_id);
    formdata.append('mapping_weight', mapping_weight);
    return this.http.post(this.baseUrl + 'inventory_mod_mapping', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  modifierInventoryMappingEdit(mapping_id,product_id, inventory_id, mapping_weight) {
    const formdata = new FormData();
    formdata.append('id', mapping_id);
    formdata.append('modifier_id', product_id);
    formdata.append('inventory_id', inventory_id);
    formdata.append('mapping_weight', mapping_weight);
    return this.http.post(this.baseUrl + 'inventory_mod_mapping_edit', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteProductInventoryMapping(mapping_id, inventory_id,product_id) {
    const formdata = new FormData();
    formdata.append('id', mapping_id);
    formdata.append('inventory_id', inventory_id);
    formdata.append('product_id', product_id);

    return this.http.post(this.baseUrl + 'remove_mapped_stock', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteModifierInventoryMapping(id,inventory_id) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('inventory_id', inventory_id);
    return this.http.post(this.baseUrl + 'remove_mapped_modifier_stock', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateStock(id, count, stock_id=null, cost=null, expiry_date=null, batch_id=null) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('count', count);
    if(stock_id){
      formdata.append('stock_id', stock_id);
    }
    if(cost){
      formdata.append('cost', cost);
    }
    if(expiry_date){
      formdata.append('expiry_date', expiry_date);
    }
    if(batch_id){
      formdata.append('batch_id', batch_id);
    }
    return this.http.post(this.baseUrl + 'update_stock', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateStockDirect(id, count, stock_id=null, cost=null, expiry_date=null, batch_id=null) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('count', count);
    if(stock_id){
      formdata.append('stock_id', stock_id);
    }
    if(cost){
      formdata.append('cost', cost);
    }
    if(expiry_date){
      formdata.append('expiry_date', expiry_date);
    }
    if(batch_id){
      formdata.append('batch_id', batch_id);
    }
    return this.http.post(this.baseUrl + 'update_stock_direct', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateInventoryConfig(data) {
    return this.http.post(this.baseUrl + 'inventory_config_update', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getInventoryConfig(id) {
    return this.http.get(this.baseUrl + 'inventory_config?inventory_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  wastage(data) {
    return this.http.post(this.baseUrl + 'wastage_add', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  inventoryWastageInventorySingle(data) {
    return this.http.post(this.baseUrl + 'wastage_add_single_inventory', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  wastageReport(from, to) {
    from = from.toISOString();
    to = to.toISOString();
    return this.http.get(this.baseUrl + 'wastage_report?from=' + from + '&to=' + to, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  wastageReportGroup(from, to) {
    from = from.toISOString();
    to = to.toISOString();
    return this.http.get(this.baseUrl + 'wastage_report_group?from=' + from + '&to=' + to, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  stockAlert(expiry) {
    return this.http.get(this.baseUrl + 'stock_alert?expiry='+expiry, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  // Check if a purchase invoice_id is already taken for the current user
  checkInvoiceId(invoice_id: string) {
    return this.http.get(this.baseUrl + 'check_invoice_id?invoice_id=' + encodeURIComponent(invoice_id), {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

 



  /////////add product orders

  addProductOrders(invoice_id, purchase_date, total_value, products, vendor_name,purchaseOrderId, vendor_id, staff_id, status, varience, notes, invoice_images_json, tax) {
    const pdate = moment(purchase_date).startOf('day').format('YYYY-MM-DD HH:mm:ss')
    const formdata = new FormData();
    formdata.append('invoice_id', invoice_id);
    formdata.append('purchase_date', pdate);
    formdata.append('total_value', total_value);
    formdata.append('cost_variance', varience);
    formdata.append('products', products);
    formdata.append('vendor_name', vendor_name);
    formdata.append('purchase_order_id', purchaseOrderId);
    formdata.append('vendor_id', vendor_id);
    formdata.append('staff_id', staff_id);
    formdata.append('status', status);
    formdata.append('notes', notes);
    formdata.append('invoice_images', invoice_images_json);
    formdata.append('tax', tax);
    return this.http.post(this.baseUrl + 'add_product_order', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  /////////add product orders

  saveProductOrderToDraft(data) {
    const pdate = moment(data.purchase_date).startOf('day').format('YYYY-MM-DD HH:mm:ss')
    const formdata = new FormData();
    formdata.append('invoice_id', data.invoice_id);
    formdata.append('purchase_date', pdate);
    formdata.append('total_value', data.total_value);
    formdata.append('cost_variance', data.cost_variance);
    formdata.append('products', data.products);
    formdata.append('vendor_name', data.vendor_name);
    formdata.append('purchase_order_id', data.purchase_order_id);
    formdata.append('vendor_id', data.vendor_id);
    formdata.append('staff_id', data.staff_id);
    formdata.append('status', data.status);
    formdata.append('notes', data.notes);
    formdata.append('invoice_images', data.invoice_images);
    formdata.append('tax', data.tax);
    return this.http.post(this.baseUrl + 'product_order_draft', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateProductOrderToDraft(id,data) {
    const pdate = moment(data.purchase_date).startOf('day').format('YYYY-MM-DD HH:mm:ss')
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('invoice_id', data.invoice_id);
    formdata.append('purchase_date', pdate);
    formdata.append('total_value', data.total_value);
    formdata.append('cost_variance', data.cost_variance);
    formdata.append('products', data.products);
    formdata.append('vendor_name', data.vendor_name);
    formdata.append('purchase_order_id', data.purchase_order_id);
    formdata.append('vendor_id', data.vendor_id);
    formdata.append('staff_id', data.staff_id);
    formdata.append('status', data.status);
    formdata.append('notes', data.notes);
    formdata.append('invoice_images', data.invoice_images);
    formdata.append('tax', data.tax);
    return this.http.post(this.baseUrl + 'product_order_draft_update', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateProductOrders(id, invoice_id, purchase_date, total_value, products, vendor_name, vendor_id, staff_id, status, varience, notes, invoice_images_json, tax) {
    const pdate = moment(purchase_date).startOf('day').format('YYYY-MM-DD HH:mm:ss')
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('invoice_id', invoice_id);
    formdata.append('purchase_date', pdate);
    formdata.append('total_value', total_value);
    formdata.append('cost_variance', varience);
    formdata.append('products', products);
    formdata.append('vendor_name', vendor_name);
    formdata.append('vendor_id', vendor_id);
    formdata.append('staff_id', staff_id);
    formdata.append('status', status);
    formdata.append('notes', notes);
    formdata.append('tax', tax);
    formdata.append('invoice_images', invoice_images_json);
    return this.http.post(this.baseUrl + 'update_product_order', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  addProductOrdersPending(invoice_id, purchase_date, total_value, products, vendor_name, vendor_id, staff_id, status, poid, notes, is_scheduled, scheduled_date, tax,delivery_at=null) {
    const pdate = moment(purchase_date).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const scheduled_date_formeted = scheduled_date?moment(scheduled_date).utc().format('YYYY-MM-DD HH:mm:ss'):null;
    let scheduled = is_scheduled ? '1' : '0';
    const formdata = new FormData();
    formdata.append('invoice_id', invoice_id);
    formdata.append('purchase_date', pdate);
    formdata.append('total_value', total_value);
    formdata.append('tax', tax);
    formdata.append('products', products);
    formdata.append('vendor_name', vendor_name);
    formdata.append('vendor_id', vendor_id);
    formdata.append('staff_id', staff_id);
    formdata.append('status', status);
    formdata.append('purchase_order_id', poid);
    if(notes){
      formdata.append('order_notes', notes);
      formdata.append('notes', notes);
    }
   
    formdata.append('is_mail_scheduled', scheduled);
    if(scheduled_date_formeted)
    {
      formdata.append('email_schedule', scheduled_date_formeted);
    }
   
    if(delivery_at){
      formdata.append('delivery_at', delivery_at);
    }
    else
    {
      formdata.append('delivery_at', new Date().toISOString());
    }
   
    return this.http.post(this.baseUrl + 'add_product_order_pending', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteProductOrdersPending(data) {
    let payload={
      id:data
    };
    return this.http.post(this.baseUrl + 'remove_product_order_pending', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  updateProductOrderMailSchedule(id, is_scheduled, scheduled_date,delivery_at=null) {
    const scheduled_date_formeted = moment(scheduled_date).utc().format('YYYY-MM-DD HH:mm:ss');
    
    let scheduled = is_scheduled ? '1' : '0';
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('is_mail_scheduled', scheduled);
    formdata.append('email_schedule', scheduled_date_formeted);
    if(delivery_at){
      const delivery_at_formated=moment(delivery_at).utc().format('YYYY-MM-DD HH:mm:ss');
      formdata.append('delivery_at', delivery_at_formated);
    }
    return this.http.post(this.baseUrl + 'product_order_mail_schedule', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  createOnlineOrderAutoDisable(start, end) {
    const from = moment(start).utc().format('YYYY-MM-DD HH:mm:ss');
    const to = moment(end).utc().format('YYYY-MM-DD HH:mm:ss');
    const formdata = new FormData();
    formdata.append('start', from);
    formdata.append('end', to);

    return this.http.post(this.baseUrl + 'online_order_auto_disable_add', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateOnlineOrderAutoDisable(id, start, end) {
    const from = moment(start).utc().format('YYYY-MM-DD HH:mm:ss');
    const to = moment(end).utc().format('YYYY-MM-DD HH:mm:ss');
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('start', from);
    formdata.append('end', to);

    return this.http.post(this.baseUrl + 'online_order_auto_disable_update', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getOnlineOrderAutoDisable() {

    return this.http.get(this.baseUrl + 'online_order_auto_disable', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getOnlineOrderAutoDisableActive() {
    return this.http.get(this.baseUrl + 'online_order_auto_disable_active', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  removeOnlineOrderAutoDisable(id: any) {
    return this.http.get(this.baseUrl + 'online_order_auto_disable_remove?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  // New Scheduled Disable API methods
  getScheduledDisable() {
    return this.http.get(this.baseUrl + 'get_scheduled_disable', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createScheduledDisable(data: any) {
    const formdata = new FormData();
    formdata.append('schedule_start_time', data.schedule_start_time);
    formdata.append('schedule_end_time', data.schedule_end_time);
    formdata.append('formatted_start_time', data.formatted_start_time);
    formdata.append('formatted_end_time', data.formatted_end_time);
    formdata.append('close_reason', data.close_reason);
    if (data.user_id) {
      formdata.append('user_id', data.user_id);
    }
    if (data.domain_type) {
      formdata.append('domain_type', data.domain_type);
    }
    
    return this.http.post(this.baseUrl + 'create_scheduled_disable', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteScheduledDisable(id: any) {
    return this.http.get(this.baseUrl + 'delete_scheduled_disable?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  checkCurrentScheduledDisable() {
    return this.http.get(this.baseUrl + 'check_current_scheduled_disable', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



  editProductOrdersPending(id, invoice_id, purchase_date, total_value, products, vendor_name, vendor_id, staff_id, status, poid, notes, is_scheduled) {
    // const pdate = moment(purchase_date).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    // const scheduled_date_formeted = moment(scheduled_date).utc().format('YYYY-MM-DD HH:mm:ss');
    let scheduled = is_scheduled ? '1' : '0';
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('invoice_id', invoice_id);
    // formdata.append('purchase_date', pdate);
    formdata.append('total_value', total_value);
    formdata.append('products', products);
    formdata.append('vendor_name', vendor_name);
    formdata.append('vendor_id', vendor_id);
    formdata.append('staff_id', staff_id);
    formdata.append('status', status);
    formdata.append('purchase_order_id', poid);
    formdata.append('order_notes', notes);
    formdata.append('notes', notes);
    formdata.append('is_mail_scheduled', scheduled);
    // formdata.append('email_schedule', scheduled_date_formeted);
    return this.http.post(this.baseUrl + 'edit_product_order_pending', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }




  //////get all product orders

  getAllProductsOrders(from_date, to_date) {
    return this.http.get(this.baseUrl + 'all_product_orders?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  getPendingProductsOrders() {
    return this.http.get(this.baseUrl + 'pending_product_orders', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }
  getProductsOrdersDrafts() {
    return this.http.get(this.baseUrl + 'product_orders_drafts', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  getVendorProductsOrders(id) {
    return this.http.get(this.baseUrl + 'product_orders_by_vendor?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  updateTableSideOrder(data) {
    return this.http.post(this.baseUrl + 'update_table_side_ordering_settings', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  getTableSideOrder() {
    return this.http.get(this.baseUrl + 'get_table_side_ordering_settings', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  getInventorySettings() {
    return this.http.get(this.baseUrl + 'inventory_settings_get', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  updateInventorySettings(data) {
    return this.http.post(this.baseUrl + 'inventory_settings_update', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  inventoryCodeCopyFromProduct() {
    return this.http.get(this.baseUrl + 'inventory_code_copy_from_product', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  getPoSettings() {
    return this.http.get(this.baseUrl + 'inventory_po_settings', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  // Inventory Alert Configuration APIs
  getInventoryAlertConfig() {
    return this.http.get(this.baseUrl + 'inventory-alerts/config', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    });
  }

  updateInventoryAlertConfig(data: any) {
    return this.http.post(this.baseUrl + 'inventory-alerts/config', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    });
  }

  toggleInventoryAlertStatus(data: { alert_type: string; is_active: boolean }) {
    return this.http.post(this.baseUrl + 'inventory-alerts/toggle-status', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    });
  }

  addInventoryAlertRecipient(data: { alert_config_id: number; email: string; name?: string }) {
    return this.http.post(this.baseUrl + 'inventory-alerts/recipients/add', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    });
  }

  removeInventoryAlertRecipient(data: { recipient_id: number }) {
    return this.http.post(this.baseUrl + 'inventory-alerts/recipients/remove', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    });
  }

  getInventoryAlertLogs(params?: { alert_type?: string; status?: string; per_page?: number; page?: number }) {
    let queryParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined) {
          queryParams = queryParams.set(key, params[key].toString());
        }
      });
    }
    return this.http.get(this.baseUrl + 'inventory-alerts/logs', {
      params: queryParams,
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    });
  }

  previewLowStockItems(params: { include_zero_stock?: boolean; include_below_alert?: boolean }) {
    let queryParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined) {
        queryParams = queryParams.set(key, params[key].toString());
      }
    });
    return this.http.get(this.baseUrl + 'inventory-alerts/preview/low-stock', {
      params: queryParams,
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    });
  }

  previewExpiringItems(params: { days_before_expiry?: number; include_expired?: boolean }) {
    let queryParams = new HttpParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined) {
        queryParams = queryParams.set(key, params[key].toString());
      }
    });
    return this.http.get(this.baseUrl + 'inventory-alerts/preview/expiring', {
      params: queryParams,
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    });
  }

  sendTestInventoryAlert(data: { alert_type: string }) {
    return this.http.post(this.baseUrl + 'inventory-alerts/send-test', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    });
  }

  getVendorDeliveryDays(id) {
    return this.http.get(this.baseUrl + 'vendor_delivery_days_get?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  updateVendorDeliveyDays(data) {
    return this.http.post(this.baseUrl + 'vendor_delivery_days_update', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }


  getVendorProductsOrdersPending(id) {
    return this.http.get(this.baseUrl + 'product_orders_by_vendor_pending?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }

  getMasterShopList() {
    return this.http.get(this.baseUrl + 'get_linked_shop_name_list', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }




  ///////////////////xlsx product import

  importProductByXlsx(dat) {
    return this.http.post(this.baseUrl + 'product_import', dat, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  importModCatByXlsx(dat) {
    return this.http.post(this.baseUrl + 'modifierscategory_import', dat, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  importModfiersByXlsx(dat) {
    return this.http.post(this.baseUrl + 'modifiers_import', dat, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  ///////////////////xlsx Table import

  importTableByXlsx(dat) {
    return this.http.post(this.baseUrl + 'table_import', dat, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  importInventoriesByXlsx(dat) {
    return this.http.post(this.baseUrl + 'inventory_import', dat, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  /////////////////ticket details

  getTicketDetails(ticket_id) {
    const formdata = new FormData();
    formdata.append('ticket_id', ticket_id);
    return this.http.post(this.baseUrl + 'ticket/orders', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  /////////////////ticket details new
  
  getTicketTotalValue(ticket_id) {
    const formdata = new FormData();
    formdata.append('ticket_id', ticket_id);
    return this.http.post(this.baseUrl + 'get_ticket_total_value', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  /////////////email invoice

  emailInvoiceTicket(document, ticket_no, company_name, target_email, target_name) {
    const formdata = new FormData();
    formdata.append('document', document);
    formdata.append('ticket_no', ticket_no);
    formdata.append('company_name', company_name);
    formdata.append('target_email', target_email);
    formdata.append('target_name', target_name);
    return this.http.post(this.baseUrl + 'ticket/mail', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  ///////////get attendance report
  getAttendance(startDate, endDate, stime, etime) {
    console.log(startDate);
    console.log(endDate);
    const start_date = moment(startDate).set({ "hour": stime.split(':')[0], "minute": stime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');
    const end_date = moment(endDate).set({ "hour": etime.split(':')[0], "minute": etime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');
    return this.http.get(this.baseUrl + 'attendance_report?startDate=' + moment(start_date).utc().format() + '&endDate=' + moment(end_date).utc().format(), {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }
  getAttendanceRecords(startDate, endDate, stime, etime) {
    console.log(startDate);
    console.log(endDate);
    const start_date = moment(startDate).set({ "hour": stime.split(':')[0], "minute": stime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');
    const end_date = moment(endDate).set({ "hour": etime.split(':')[0], "minute": etime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');
    return this.http.get(this.baseUrl + 'attendance_records?startDate=' + moment(start_date).utc().format() + '&endDate=' + moment(end_date).utc().format(), {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      })
    })
  }




  ///////get QR codes

  getTableQr() {
    return this.http.get(this.baseUrl + 'getTableQr', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getUserStatusForPosStatus() {
    return this.http.get(this.baseUrl + 'get_user_status', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getAllTerminals() {
    return this.http.post(this.baseUrl + 'getallterminal', '', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  setPosStatus(enable_pos) {
    const formdata = new FormData();
    formdata.append('enable_pos', enable_pos);
    return this.http.post(this.baseUrl + 'set_pos_status', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  setMainTerminal(terminal) {
    const formdata = new FormData();
    formdata.append('terminal', terminal);
    return this.http.post(this.baseUrl + 'set_main_terminal', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  uploadFile(file) {
    const formdata = new FormData();
    formdata.append('file', file);
    return this.http.post(this.baseUrl + 'fileUpload', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  imageUpload(file) {
    const formdata = new FormData();
    formdata.append('image', file);
    return this.http.post(this.baseUrl + 'image_upload', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }




  excelImportBulkProducts(file) {
    const formdata = new FormData();
    formdata.append('file', file);
    return this.http.post(this.baseUrl + 'product_import_excel', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  excelImportBulkProductsAsync(file) {
    const formdata = new FormData();
    formdata.append('file', file);
    return this.http.post(this.baseUrl + 'product_import_excel_async', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getExcelImportJobStatus(jobId: string): Observable<any> {
    return this.http.get(this.baseUrl + 'products/import-excel/status/' + jobId, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getExcelImportJobs(): Observable<any> {
    return this.http.get(this.baseUrl + 'products/import-excel/jobs', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  //////////////////////////////summary 


  customerImport(file) {
    const formdata = new FormData();
    formdata.append('file', file);
    return this.http.post(this.baseUrl + 'customers/import', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



// Departmant wise report



  getDepartmentWiseReport(from_date, to_date) {
    let data = {
      "start_date_time": from_date,
      "end_date_time": to_date,
    };
    return this.http.post(this.baseUrl + 'department_wise_sales',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getDepartmentWiseHourlyReport(from_date, to_date) {
    let data = {
      "start_date_time": from_date,
      "end_date_time": to_date,
    };
    return this.http.post(this.baseUrl + 'department_wise_sales_by_hour',data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



  // get hourly report


  // get cash drawe log

  getDrawerReport(from_date, to_date) {
    return this.http.get(this.baseUrl + 'drawerlog?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCashinoutReport(from_date, to_date) {
    return this.http.get(this.baseUrl + 'cashinout_report?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  //Get void tickets
  getVoidTickets(from_date, to_date) {
    return this.http.get(this.baseUrl + 'void_report?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  //sync
  forcePush() {
    const formdata = new FormData();
    formdata.append('data', 'FORCEPUSH');
    return this.http.post(this.baseUrl + 'forcepush', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getSyncStatus() {
    return this.http.get(this.baseUrl + 'get_sync_status', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }




  createProModcatMap(productCat, modifierCat) {

    const formdata = new FormData();
    formdata.append('product_id', productCat);
    formdata.append('modfiercat_id', modifierCat);

    return this.http.post(
      this.baseUrl + 'add_prod_modcat_mapping',
      formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  editProModcatMap(id, productCat, modifierCat) {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('category_id', productCat);
    formdata.append('modifierscategory_id', modifierCat);
    formdata.append('forced_count', '1');
    return this.http.post(
      this.baseUrl + 'productmodifiersmapping_edit',
      formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getProModcatMap() {
    return this.http.get(this.baseUrl + 'get_prod_modcat_mapping', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getProModcatMapAll() {
    return this.http.get(this.baseUrl + 'get_prod_modcat_mapping_all', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProModifierMapAll() {
    return this.http.get(this.baseUrl + 'modifier_map_to_products_all', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProbyModcatMap(id) {

    return this.http.get(this.baseUrl + 'get_prod_bymodcat?modfiercat_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteProModcatMap(id) {

    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(
      this.baseUrl + 'delete_prod_modcat_mapping',
      formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  getCustomers() {
    return this.http.get(
      this.baseUrl + 'customersall',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getCustomersByid(id:any) {
    return this.http.get(
      this.baseUrl + 'customer_by_id?id='+id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getCustomerConsentTransaction(email:any) {
    return this.http.get(
      this.baseUrl + 'customer_consent_transactions?email='+email,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getCustomersConsents() {
    return this.http.get(
      this.baseUrl + 'customer_consent_marketing',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  setCustomerMembership(data) {
    return this.http.post(
      this.baseUrl + 'set_customer_membership',data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  addCustomerLoyaltyPoints(data) {
    return this.http.post(
      this.baseUrl + 'add_loyalty_points_to_customer',data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  
  getCustomerReports(id, from, to) {
    return this.http.get(
      this.baseUrl + 'customer_report?customer_id=' + id + '&from=' + from + '&to=' + to,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }



  createCustomer(name, email, tel, addressLine1, zipCode) {
    console.log(name);
    console.log(email);
    console.log(tel);
    console.log(addressLine1);

    const formdata = new FormData();
    formdata.append('name', name)
    formdata.append('tel', tel)

    formdata.append('addressLine1', addressLine1)
    formdata.append('email', email)
    formdata.append('zipCode', zipCode)

    return this.http.post(
      this.baseUrl + 'add_customer', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  editCustomer(id, name, email, tel, addressLine1, zipCode) {
    const formdata = new FormData();

    formdata.append('customer_id', id)
    formdata.append('tel', tel)
    formdata.append('name', name)
    formdata.append('addressLine1', addressLine1)
    formdata.append('email', email)
    formdata.append('zipCode', zipCode)

    return this.http.post(
      this.baseUrl + 'edit_customer', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  customerWalletTransactions(id) {
    return this.http.get(
      this.baseUrl + 'customer_wallet_transactions?customer_id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  customerMembershipTypes() {
    return this.http.get(
      this.baseUrl + 'customer_membership_type_list',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  customerMembershipTypeCreate(data) {
    return this.http.post(
      this.baseUrl + 'customer_membership_type_create',data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  customerMembershipTypeUpdate(id,data) {
    return this.http.post(
      this.baseUrl + 'customer_membership_type_update/'+id,data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  customerMembershipTypeDelete(id) {
    return this.http.post(
      this.baseUrl + 'customer_membership_type_delete/'+id,{},
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  deleteCustomer(id) {
    const formdata = new FormData();
    formdata.append('id', id);
    return this.http.post(
      this.baseUrl + 'delete_customer', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  getCustomerTickets(id, start, end) {
    const start_date = moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const end_date = moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss');




    return this.http.get(
      this.baseUrl +
      'get_all_customer_tickets?customer_id=' + id + '&from_date=' +
      moment(start_date).utc().format() +
      '&&to_date=' +
      moment(end_date).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getCustomerUnpaidTickets(id) {
    return this.http.get(
      this.baseUrl +
      'pay_later_tickets?customer_id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getCustomerTransaction(id, from, to) {
    return this.http.get(
      this.baseUrl +
      'get_payment_records?customer_id=' + id + '&from=' + moment(from).utc().format() + '&to=' + moment(to).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  getCombo() {
    return this.http.get(this.baseUrl + 'get_combo',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  getCombosPaginated(payload: {
    per_page?: number;
    page?: number;
    search?: string;
  }): Observable<any> {
    return this.http.post(this.baseUrl + 'combos_search_paginated', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProductCombo() {
    return this.http.get(this.baseUrl + 'get_product_combo',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  comboStatusUpdate(data) {
    return this.http.post(this.baseUrl + 'combo_active_update', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  getComboMappings(id) {
    return this.http.get(this.baseUrl + 'get_combo_mappings?combo_id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }




  deleteCompoCat(id) {
    const formdata = new FormData();
    formdata.append("id", id);
    return this.http.post(this.baseUrl + 'delete_combo_cat_map', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )

  }
  deleteCompoProd(id) {
    const formdata = new FormData();
    formdata.append("id", id);
    return this.http.post(this.baseUrl + 'delete_combo_prod_map', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )

  }



  addCombo(val, img,availabilityData) {
    const start_date = moment(val.startdate).format('YYYY-MM-DD');
    const end_date = moment(val.enddate).format('YYYY-MM-DD');
    const formdata = new FormData();
    formdata.append("image", img);
    formdata.append("combo_name", val.comboname);
    formdata.append("description", val.description);
    formdata.append("startdate", start_date);
    formdata.append("enddate", end_date);
    formdata.append("combo_price", val.comboprice);
    formdata.append("combo_cat_id", val.combocat);
    formdata.append("is_product_combo", val.is_product_combo);
    formdata.append("table_price", val.table_price != null ? val.table_price : val.comboprice);
    formdata.append("availability", JSON.stringify(availabilityData));
    formdata.append("banner", val.banner);
    if (val.pricetags && Array.isArray(val.pricetags)) {
      const comboPricetags = val.pricetags
        .filter((p) => p && p.id != null && p.price !== '' && p.price != null)
        .map((p) => ({ id: p.id, price: p.price }));
      formdata.append('pricetags', JSON.stringify(comboPricetags));
    }
    return this.http.post(this.baseUrl + 'add_combo', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }


  deleteCombo(id) {
    const formdata = new FormData();
    formdata.append("combo_id", id);

    return this.http.post(this.baseUrl + 'delete_combo', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  editCombo(id, val, img,availabilityData) {
    const start_date = moment(val.startdate).format('YYYY-MM-DD');
    const end_date = moment(val.enddate).format('YYYY-MM-DD');
    const formdata = new FormData();
    formdata.append("combo_id", id);
    formdata.append("image", img);
    formdata.append("combo_name", val.comboname);
    formdata.append("description", val.description);
    formdata.append("startdate", start_date);
    formdata.append("enddate", end_date);
    formdata.append("combo_price", val.comboprice);
    formdata.append("combo_cat_id", val.combocat);
    formdata.append("is_product_combo", val.is_product_combo);
    formdata.append("table_price", val.table_price != null ? val.table_price : val.comboprice);
    formdata.append("availability", JSON.stringify(availabilityData));
    formdata.append("banner", val.banner);
    if (val.pricetags && Array.isArray(val.pricetags)) {
      const comboPricetags = val.pricetags
        .filter((p) => p && p.id != null && p.price !== '' && p.price != null)
        .map((p) => ({ id: p.id, price: p.price }));
      formdata.append('pricetags', JSON.stringify(comboPricetags));
    }
    return this.http.post(this.baseUrl + 'edit_combo', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }




  addComboCat(id, val) {
    const formdata = new FormData();
    formdata.append("combo_id", id);
    formdata.append("cat_id", val.cat_id);
    formdata.append("min", val.min);
    formdata.append("max", val.max);
    formdata.append("allow_same", val.allow_same ? '1' : '0');
    console.log(formdata);

    return this.http.post(this.baseUrl + 'add_combo_cat_map', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  addComboProd(id, val) {
    const formdata = new FormData();
    formdata.append("product_id", val.product_id);
    formdata.append("combo_id", id);
    formdata.append("min", val.min);
    formdata.append("max", val.max);
    formdata.append("allow_same", val.allow_same ? '1' : '0');
    return this.http.post(this.baseUrl + 'add_combo_prod_map', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }





  editCombocat(val) {
    const formdata = new FormData();
    formdata.append("id", val.id);
    formdata.append("max", val.max);
    formdata.append("min", val.min);
    formdata.append("allow_same", val.allow_same ? '1' : '0');
    return this.http.post(this.baseUrl + 'edit_combo_cat_map', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  editComboprod(val) {
    const formdata = new FormData();
    formdata.append("id", val.id);
    formdata.append("max", val.max);
    formdata.append("min", val.min);
    formdata.append("allow_same", val.allow_same ? '1' : '0');
    return this.http.post(this.baseUrl + 'edit_combo_prod_map', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }


  eatUberAuth() {

    const formdata = new FormData();
    formdata.append("client_secret", "qe7_AbIPFZXlCWYUNGSpURyzA5RuQKMZVLdF0gqo");
    formdata.append("client_id", "U9Fuyt-gOITHKZJK1gLvR3EEfwwcPYIg");
    formdata.append("grant_type", "authorization_code");
    formdata.append("scope", "pos_provisioning");


    return this.http.post('https://login.uber.com/oauth/v2/authorize', formdata, {
      headers: {
        'X-Skip-Interceptor': 'true', // Custom header to indicate skipping the interceptor
      },
    }
  );
  }

  getPosStatus() {
    let id = "065896b1-9b82-415c-a178-46273b642374";
    return this.http.get('https://api.uber.com/v1/eats/stores/' + id + '/pos_data',
      {
        headers:{
          'X-Skip-Interceptor': 'true', // Custom header to indicate skipping the interceptor
        },
      },
    )
  }




  getCashup(from, to) {
    //  from = moment(from).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    //  to = moment(to).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    console.log(" start :", from);
    console.log(" end :", to);
    return this.http.get(this.baseUrl + 'get_cash_up?from_date=' + from + '&to_date=' + to,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  getSurcharge() {
    return this.http.get(this.baseUrl + 'get_surcharge',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  getPosAppVersionStatus(){
    return this.http.get(this.baseUrl + 'get_pos_app_version_status',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }
  
  getDomains(){
    return this.http.get(this.baseUrl + 'domains_admin',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  getPricetagOrders(){
    return this.http.get(this.baseUrl + 'pricetagorder',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  storePricetagOrder(domainId: number, items: Array<{
    pricetags_id: number;
    sort_order: number;
    check_membership?: boolean;
    is_active?: boolean;
    override?: boolean;
  }>) {
    return this.http.post(this.baseUrl + 'pricetagorder', { domain_id: domainId, items }, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updatePricetagOrderCheckMembership(pricetagorderId: number, checkMembership: boolean) {
    return this.http.patch(
      this.baseUrl + 'pricetagorder/' + pricetagorderId + '/check_membership',
      { check_membership: checkMembership },
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      }
    );
  }

  editSurcharge(data) {
    let dat = {
      label: data.label,
      value: data.value,
      id: data.id,
      availability: JSON.stringify(data.availability)
    }
    console.log("id check", data.id);

    return this.http.post(this.baseUrl + 'edit_surcharge', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  deleteSurcharge(data) {
    let dat = {
      id: data.id
    }
    return this.http.post(this.baseUrl + 'delete_surcharge', dat,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }

  addSurcharge(data) {
    let dat = {
      label: data.label,
      value: data.value,
      availability: JSON.stringify(data.availability)
    }
    return this.http.post(this.baseUrl + 'add_surcharge', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    )
  }




  // promotions 

  getPromotions() {

    return this.http.get(this.baseUrl + 'get_promotions',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }


  addPromotion(data) {
    return this.http.post(this.baseUrl + 'add_promotion', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  promoStatusUpdate(data) {
    return this.http.post(this.baseUrl + 'active_update', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  customerpromoStatusUpdate(data) {
    return this.http.post(this.baseUrl + 'customer-promotions-enable', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }
  customerpromoUpdate(data, id) {
    return this.http.post(this.baseUrl + 'customer-promotions-update/' + id, data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }
  customerpromoDelete(id) {
    return this.http.post(this.baseUrl + 'customer-promotions-delete/' + id, null,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }
  deletePromotion(data) {
    return this.http.post(this.baseUrl + 'delete_promotions', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })

  }

  deletePromotionrule(data) {

    return this.http.post(this.baseUrl + 'delete_promotion_rule', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }


  updatePromotion(data) {
    return this.http.post(this.baseUrl + 'update_promotion', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }


  getModifiersPrefix() {
    return this.http.get(this.baseUrl + 'get_modifier_prefix',

      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  addModifiersPrefix(data) {
    return this.http.post(this.baseUrl + 'add_modifier_prefix', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  deleteModifiersPrefix(data) {
    return this.http.post(this.baseUrl + 'delete_modifier_prefix', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  updateModifiersPrefix(data) {
    return this.http.post(this.baseUrl + 'edit_modifier_prefix', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  getStripeAccId() {
    return this.http.get(this.baseUrl + 'getStripeAccId',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  getGatewayConfig() {
    return this.http.get(this.baseUrl + 'get_gateway_config',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }
  getPaymentGatewaystypes() {
    return this.http.get(this.baseUrl + 'get_gateways',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }


  addStripeAccId(data) {
    return this.http.post(this.baseUrl + 'addStripeAccId', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  updateOnlineSettings(data) {
    return this.http.post(this.baseUrl + 'update_online_settings', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }


  confirmPassword(pass) {
    return this.http.post(this.baseUrl + 'confirmpass', pass,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }


  getOnlineMetadata() {
    return this.http.get(this.baseUrl + 'getonlineMetadataAdmin',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })

  }
  getInvoiceData() {
    return this.http.get(this.baseUrl + 'get_invoice_data',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })

  }
  updateInvoiceData(data) {
    return this.http.post(this.baseUrl + 'update_invoice_data', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })

  }

  getUserOnlineMetaDataUpdate(add) {

    const formdata = new FormData();

    // formdata.append('is_closed', add.is_closed);
    formdata.append('delivery_buffer_time', add.delivery_buffer_time);
    formdata.append('pickup_buffer_time', add.pickup_buffer_time);
    formdata.append('pre_min_day', add.pre_min_day);
    formdata.append('online_inactive_reason', add.online_inactive_reason);
    formdata.append('website', add.website);
    formdata.append('dine_in', add.dine_in ? '1' : '0');
    formdata.append('pickup', add.pickup ? '1' : '0');
    formdata.append('is_preorder', add.is_preorder ? '1' : '0');
    formdata.append('online_order_active', add.online_order_active ? '1' : '0');
    formdata.append('delivery', add.delivery ? '1' : '0');
    formdata.append('table_reservation', add.tablereserve ? '1' : '0');
    formdata.append('tax', add.tax);
    formdata.append('online_payment_enable', add.online_payment_enable ? '1' : '0');
    formdata.append('surcharge_enable', add.surcharge_enable ? '1' : '0');
    formdata.append('surcharge_percent', add.surcharge_percent);
    formdata.append('surcharge_amount', add.surcharge_amount);


    return this.http.post(this.baseUrl + 'updateonlineMetadataAdmin', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



  getNotificationEmails() {
    return this.http.get(this.baseUrl + 'get_notification_email',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  addNotificationEmail(payload) {
    const formdata = new FormData();
    formdata.append('email', payload.email);
    formdata.append('online_order_recieved', payload.online_order_recieved);
    formdata.append('purchase_order_invoice', payload.purchase_order_invoice );
    formdata.append('purchase_order_invoice_send', payload.purchase_order_invoice_send);
    formdata.append('shift_end_report', payload.shift_end_report);
    formdata.append('table_reservation', payload.table_reservation);
    formdata.append('void_notification', payload.void_notification);
    formdata.append('inventory_wastage', payload.inventory_wastage);
    formdata.append('online_order_status', payload.online_order_status);
    formdata.append('online_delivery_status', payload.online_delivery_status);
    formdata.append('online_pickup_status', payload.online_pickup_status);
    formdata.append('external_delivery_status', payload.external_delivery_status);
    formdata.append('online_payment_status', payload.online_payment_status);


    return this.http.post(this.baseUrl + 'add_notification_email', formdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  deleteNotificationEmail(data) {
    return this.http.post(this.baseUrl + 'delete_notification_email', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  updateNotificationEmail(data) {
    return this.http.post(this.baseUrl + 'update_notification_email', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }



  getOnlinelist(from_date, to_date) {
    return this.http.get(this.baseUrl + 'get_online_order_list?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



  updateShopImage(data) {
    return this.http.post(this.baseUrl + 'update_shop_image', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }


  getOnlineorderData(id) {
    return this.http.get(this.baseUrl + 'get_onlineOrder_data?order_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  checkIntegrationAvailable(type: string) {
    return this.http.get(this.baseUrl + 'check_delivery_integration?type=' + type, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  initiateQuoteAndDeliveryDoorDash(data: any) {
    return this.http.post(this.baseUrl + 'initiate_quote_and_delivery_admin', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  initiateQuoteAndDeliveryUberDirect(data: any) {
    return this.http.post(this.baseUrl + 'uber_direct_order_retry', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



  getholidays() {
    return this.http.get(this.baseUrl + 'shop_holyday_get', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  addHoliday(data) {

    return this.http.post(this.baseUrl + 'shop_holyday_add', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }


  deleteHoliday(data) {

    return this.http.post(this.baseUrl + 'shop_holyday_delete', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  editHoliday(data) {

    return this.http.post(this.baseUrl + 'shop_holyday_edit', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  addCombogroup(data) {
    return this.http.post(this.baseUrl + 'combogroups_add', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }
  editCombogroup(data) {
    return this.http.post(this.baseUrl + 'combogroups_edit', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      })
  }

  getCombogroups(id) {
    return this.http.get(this.baseUrl + 'combogroups_get?id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }
  deleteCombogroup(gid) {
    let data = {
      id: gid
    }
    return this.http.post(this.baseUrl + 'combogroups_delete', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  getComboGroupsSort(id) {
    return this.http.get(this.baseUrl + 'combogroups_sort?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  saveComboGroupsSort(data) {
    return this.http.post(this.baseUrl + 'combogroups_sort', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCategoriesandProducts() {
    return this.http.get(this.baseUrl + 'category_list',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  getCategoriesandProductsWithMaster() {
    return this.http.get(this.baseUrl + 'category_list_with_master',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  category_list_paginated(params: {
    per_page?: number;
    page?: number;
    search?: string;
    include_master?: boolean;
  }): Observable<any> {
    let httpParams = new HttpParams();
    
    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.per_page) {
      httpParams = httpParams.set('per_page', params.per_page.toString());
    }
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.include_master !== undefined) {
      httpParams = httpParams.set('include_master', params.include_master.toString());
    }
    
    return this.http.get(this.baseUrl + 'category_list_paginated', {
      params: httpParams,
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCategoriesWithPaginatedProducts(params: {
    per_page?: number;
    page?: number;
    products_per_category?: number;
    search?: string;
    include_master?: boolean;
  }): Observable<any> {
    let httpParams = new HttpParams();
    
    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.per_page) {
      httpParams = httpParams.set('per_page', params.per_page.toString());
    }
    if (params.products_per_category) {
      httpParams = httpParams.set('products_per_category', params.products_per_category.toString());
    }
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.include_master !== undefined) {
      httpParams = httpParams.set('include_master', params.include_master.toString());
    }
    
    return this.http.get(this.baseUrl + 'categories/paginated-products', {
      params: httpParams,
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCategoryProductsNextPage(params: {
    category_id: number;
    page?: number;
    per_page?: number;
    search?: string;
    subcategory_id?: number | null;
    include_master?: boolean;
  }): Observable<any> {
    let httpParams = new HttpParams();
    
    httpParams = httpParams.set('category_id', params.category_id.toString());
    
    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.per_page) {
      httpParams = httpParams.set('per_page', params.per_page.toString());
    }
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.subcategory_id !== undefined && params.subcategory_id !== null) {
      httpParams = httpParams.set('subcategory_id', params.subcategory_id.toString());
    }
    if (params.include_master !== undefined) {
      httpParams = httpParams.set('include_master', params.include_master.toString());
    }
    
    return this.http.get(this.baseUrl + 'categories/products/next', {
      params: httpParams,
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getSubcategoryProductsNextPage(params: {
    subcategory_id: number;
    page?: number;
    per_page?: number;
    search?: string;
    include_master?: boolean;
  }): Observable<any> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('subcategory_id', params.subcategory_id.toString());
    if (params.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.per_page) {
      httpParams = httpParams.set('per_page', params.per_page.toString());
    }
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.include_master !== undefined) {
      httpParams = httpParams.set('include_master', params.include_master.toString());
    }
    return this.http.get(this.baseUrl + 'subcategories/products/next', {
      params: httpParams,
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  mapCombogroupstoProducts(data) {
    return this.http.post(this.baseUrl + 'combogroup_product_mapping_add', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  editmapCombogroupstoProducts(data) {
    return this.http.post(this.baseUrl + 'combogroup_product_mapping_edit', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  removeSingleProductFromGroupMapping(data: { product_id: number; group_id: number }) {
    const fromdata = new FormData();
    fromdata.append('product_id', data.product_id.toString());
    fromdata.append('group_id', data.group_id.toString());
    return this.http.post(this.baseUrl + 'combogroup_product_mapping_remove_single_product', fromdata,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  getComboGroupdetails(id: any) {
    return this.http.get(this.baseUrl + 'combo_group_details?group_id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  getCoupons() {
    return this.http.get(this.baseUrl + 'coupon_get_admin',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }
  getOfferGeneratedCoupons() {
    return this.http.get(this.baseUrl + 'offer_coupon_get_admin',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }



  getCouponbyId(id) {
    return this.http.get(this.baseUrl + 'coupon_get_by_id?id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  addCoupon(data: any) {
    return this.http.post(this.baseUrl + 'coupon_add', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });

  }

  editCoupon(data: any) {
    return this.http.post(this.baseUrl + 'coupon_edit', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  deleteCoupon(data: any) {
    return this.http.post(this.baseUrl + 'coupon_delete', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  couponExposed(data: any) {
    return this.http.post(this.baseUrl + 'exposed_update', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  couponKiosk(data: any) {
    return this.http.post(this.baseUrl + 'kiosk_update', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }
  couponReport() {
    return this.http.get(this.baseUrl + 'coupon_report',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }
  couponReportByid(id:any,start:any,end:any) {
    return this.http.get(this.baseUrl + 'coupon_report_by_id?id='+id+"&from_date="+start+"&to_date="+end,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }
  couponReportByemail(email:any,start:any,end:any) {
    return this.http.get(this.baseUrl + 'coupon_report_by_email?email='+email+"&from_date="+start+"&to_date="+end,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }



 createOffer(data: any) {
    return this.http.post(this.baseUrl + 'offer_create', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });

  }

  updateOffer(id: any,data: any) {
    return this.http.post(this.baseUrl + 'offer_update/'+id, data,
      {
        headers: new HttpHeaders({      
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  deleteOffer(id: any) {
    let data = {
      id: id
    }
    return this.http.post(this.baseUrl + 'offer_delete', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

 getOfferOfUser() {
    return this.http.get(this.baseUrl + 'get_offers_list',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });

  }
 getOfferTransactions(page: number = 1, perPage: number = 15) {
    return this.http.get(this.baseUrl + `offer-transactions?page=${page}&per_page=${perPage}`,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });

  }


  getcomboCategories() {
    return this.http.get(this.baseUrl + 'list_combo_cat_app',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  createcomboCategory(data: any) {
    return this.http.post(this.baseUrl + 'add_combo_cat', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  editcomboCategory(data: any) {
    return this.http.post(this.baseUrl + 'edit_combo_cat', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }

  deletecomboCategory(data: any) {
    return this.http.post(this.baseUrl + 'delete_combo_cat', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }
  deletecomboCategoryMapped(data: any) {
    return this.http.post(this.baseUrl + 'delete_combo_cat_mapped_data', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
  }



  reportMail(start: Date, stime: any, end: Date, etime: any) {

    const start_date = moment(start).set({ "hour": stime.split(':')[0], "minute": stime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');
    const end_date = moment(end).set({ "hour": etime.split(':')[0], "minute": etime.split(':')[1] }).format('YYYY-MM-DD HH:mm:ss');

    return this.http.get(this.baseUrl + 'testmailr?from_date=' + moment(start_date).utc().format() +
      '&&to_date=' +
      moment(end_date).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );

  }



  reportmailConfigGet() {

    return this.http.get(this.baseUrl + 'report_mail_config_get',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  reportmailConfigCreate(data: any) {

    return this.http.post(this.baseUrl + 'report_mail_config_create', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  reportmailConfigDelete(id: any) {
    let data = {
      id: id
    }
    return this.http.post(this.baseUrl + 'report_mail_config_delete', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  reportmailConfigUpdate(data: any) {

    return this.http.post(this.baseUrl + 'report_mail_config_update', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getVendorList() {
    return this.http.get(this.baseUrl + 'vendor_get',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getVendorMetadata(id) {
    return this.http.get(this.baseUrl + 'vendor_metadata_get?vendor_id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  updateVendorMetadata(data: any) {
    return this.http.post(this.baseUrl + 'vendor_metadata_update', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  externalDeliveryToggle(data: any) {
    return this.http.post(this.baseUrl + 'external_delivery_toggle', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  externalDeliveryStatus() {
    return this.http.get(this.baseUrl + 'external_delivery_status',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  createVendor(data: any) {
    return this.http.post(this.baseUrl + 'vendor_create', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  updateVendor(data: any) {
    return this.http.post(this.baseUrl + 'vendor_update', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }



  deleteVendor(id: any) {
    let data = {
      id: id
    }
    return this.http.post(this.baseUrl + 'vendor_delete', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }



  discountReport(){

    let payload={
      "start_date": 1628291572328,
      "end_date": 1718348429019,
      "grouped": true
  }
    return this.http.post(this.baseUrl + 'discount_report_for_ticket_and_order',payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  couponSummeryReport(start,end){

    let payload={
      "start_date": start,
      "end_date": end,
      "return_response": "json"
  };
    return this.http.post(this.baseUrl + 'coupon_summary_detail_report',payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  couponReportwithId(){
    let payload={
      "start_date": "2022-06-13 06:40:21",
      "end_date": "2024-05-28 04:55:31",
      "coupon_id": 45,
      "return_response": "excel"
  }
    return this.http.post(this.baseUrl + 'coupon_wise_detail_report',payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  couponReportByCustomer(){
    let payload={
      "start_date": "2022-06-13 06:40:21",
      "end_date": "2024-05-28 04:55:31",
      "coupon_id": 45,
      "return_response": "excel"
  }
    return this.http.post(this.baseUrl + 'customer_wise_coupon_report',payload,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }



  getVendorMapList() {
    return this.http.get(this.baseUrl + 'vendor_map_get',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  createVendorMap(data: any) {
    return this.http.post(this.baseUrl + 'vendor_map_create', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  updateVendorMap(data: any) {
    return this.http.post(this.baseUrl + 'vendor_map_update', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  deleteVendorMap(id: any) {
    let data = {
      id: id
    }
    return this.http.post(this.baseUrl + 'vendor_map_delete', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }






  getVendorbyInventory(id) {
    return this.http.get(this.baseUrl + 'vendor_map_by_inventory?id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getInventoryStocks(id) {
    return this.http.get(this.baseUrl + 'get_inventory_stocks?id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  deleteInventoryStocks(id,inventory_id) {
    return this.http.get(this.baseUrl + 'delete_inventory_stock_record?id=' + id+'&inventory_id=' + inventory_id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getInventoryStockList(show_zero,selectedInventory) {
    show_zero = show_zero ? 1 : 0;
    if(selectedInventory){
      return this.http.get(this.baseUrl + 'get_inventory_stock_list?show_zero=' + show_zero + '&inventory_id=' + selectedInventory,
        {
          headers: new HttpHeaders({
            Authorization: 'Bearer ' + this.authService.get().getValue(),
          }),
        },
      );
    }
    else{
      return this.http.get(this.baseUrl + 'get_inventory_stock_list?show_zero=' + show_zero,
        {
          headers: new HttpHeaders({
            Authorization: 'Bearer ' + this.authService.get().getValue(),
          }),
        },
      );
    }
    
  }

  mergeStocks() {
    return this.http.get(this.baseUrl + 'mergestocks', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  mergeStocksByInventory(inventory_id: any) {
    const formdata = new FormData();
    formdata.append('inventory_id', inventory_id);
    return this.http.post(this.baseUrl + 'mergestock_by_inventory', formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getInventoryByVendor(id) {
    return this.http.get(this.baseUrl + 'inventory_by_vendor?id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  saleStockReflection() {
    return this.http.get(this.baseUrl + 'sale_stock_reflection',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  inventoryConsumption() {
    return this.http.get(this.baseUrl + 'inventory_consumption', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  // new method by removing stock table duplication
  getInventoryByVendorId(id) {
    return this.http.get(this.baseUrl + 'inventory_by_vendor_id?id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  getvendorPurchaseList(id: any) {
    return this.http.get(this.baseUrl + 'vendor_purchase_list?id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  sendvendorPurchaseList(data: any) {
    let dat = {
      data: data
    };
    return this.http.post(this.baseUrl + 'vendor_purchase_mail', dat,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }



  getInventoryMappedProducts(id: any) {
    return this.http.get(this.baseUrl + 'inventory_mapped_products?id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getInventoryMappedModifiers(id: any) {
    return this.http.get(this.baseUrl + 'inventory_mapped_modifiers?id=' + id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  inventoriesByStockTake(type) {
    let data = { 'type': type }
    return this.http.post(this.baseUrl + 'inventories_by_stock_take', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  create_stock_take(data) {

    return this.http.post(this.baseUrl + 'create_stock_take', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  stock_take_cost_refresh(data) {

    return this.http.post(this.baseUrl + 'stock_take_cost_refresh', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  get_stock_take_log(start,end) {

    let data={
      startDate: start,
      endDate: end
    }

    return this.http.post(this.baseUrl + 'get_stock_take_log',data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  get_combo_report(start: Date, end: Date) {
    // const start_date = moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    return this.http.get(this.baseUrl + 'comboreport?from_date=' +
      moment(start).utc().format() +
      '&&to_date=' +
      moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  importSuppliersByXlsx(dat) {
    return this.http.post(this.baseUrl + 'vendor_import', dat, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



  createProductWastage(data: any) {
    return this.http.post(this.baseUrl + 'product_wastage_create', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getProductWastages(from, to) {

    // const start_date = moment(from).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    // const end_date = moment(to).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    from = from.toISOString();
    to = to.toISOString();
    return this.http.get(this.baseUrl + 'get_product_wastage?from=' + from + '&to=' + to,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getProductWastagesGroup(from, to) {

    from = from.toISOString();
    to = to.toISOString();
    return this.http.get(this.baseUrl + 'get_product_wastage_group?from=' + from + '&to=' + to,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  importInventoriesByvendorXls(dat) {
    return this.http.post(this.baseUrl + 'inventorybyvendor_import', dat, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  onlineOrderStatusRetry(dat) {
    return this.http.get(this.baseUrl + 'retry_online_order_payment_status_check?order_id=' + dat, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getShopShifts(from: any, to: any) {
    return this.http.get(this.baseUrl + 'get_shifts?from=' + from + '&to=' + to, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getShopShiftById(id: any) {
    return this.http.get(this.baseUrl + 'get_shiftbyId?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getShopShiftByIdGroups(start: any, end: any) {
    return this.http.get(this.baseUrl + 'get_shiftbyId_groups?start=' + start + '&end=' + end, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCashupsByShifts(id: any) {
    return this.http.get(this.baseUrl + 'cashup_by_shift?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getCashupsByShiftsGroup(id1: any, id2: any, start, end) {
    return this.http.get(this.baseUrl + 'cashup_by_shift_group?id1=' + id1 + '&id2=' + id2 + '&start=' + start + '&end=' + end, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getQuickNotes() {
    return this.http.get(this.baseUrl + 'get_quick_note', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createQuickNote(data: any) {
    return this.http.post(this.baseUrl + 'create_quick_note', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteQuickNote(data: any) {
    return this.http.post(this.baseUrl + 'delete_quick_note', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  createUpdateWebBuilder(data: any) {
    return this.http.post(this.baseUrl + 'website_builder', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getWebBuilderData() {
    return this.http.get(this.baseUrl + 'get_web_data', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteWebBuilderData(id: any) {
    return this.http.get(this.baseUrl + 'delete_web_data?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getinventoryProductMappingList() {
    return this.http.get(this.baseUrl + 'inventoryProductMappingList', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  inventoryProductMappingImport(data) {
    return this.http.post(this.baseUrl + 'inventoryProductMappingImport', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  combosImport(data) {
    return this.http.post(this.baseUrl + 'combos_import', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateComboPositions(payload: Array<{ id: number | string; position: number }>) {
    return this.http.post(
      this.baseUrl + 'combos/update-positions',
      { positions: payload },
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
          'Content-Type': 'application/json',
        }),
      },
    );
  }

  StaffLoginUpdate(data) {
    return this.http.post(this.baseUrl + 'staff_login_update', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProductsComboMapping(id: any) {
    return this.http.get(this.baseUrl + 'product_combo_mappingby_id?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createProductsComboMapping(data: any) {
    return this.http.post(this.baseUrl + 'product_combo_mapping_create', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteProductComboMapping(id) {
    return this.http.get(this.baseUrl + 'product_combo_mapping_delete?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getKioskImages() {
    return this.http.get(this.baseUrl + 'kiosk_images', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteKioskImage(id) {
    return this.http.get(this.baseUrl + 'delete_kiosk_images?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  addKioskImage(data) {
    return this.http.post(this.baseUrl + 'image_store', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateKioskImage(data) {
    return this.http.post(this.baseUrl + 'image_store_update', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getPricetagsall() {
    return this.http.get(this.baseUrl + 'pricetag_all', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getPricetagsallWithMembership() {
    return this.http.get(this.baseUrl + 'pricetag_with_membership_details', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  validateStaffPin(data: any) {
    return this.http.post(this.baseUrl + 'validate_staff_pin', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getStaffNames() {
    return this.http.get(this.baseUrl + 'getStaffNames', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  gettableBookings(from_date, to_date) {
    return this.http.get(this.baseUrl + 'get_table_booking?from_date=' + from_date + '&to_date=' + to_date, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  gettableBookingsToday() {
    return this.http.get(this.baseUrl + 'get_bookings_today', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  exportPdftableBookingsByDate(data) {
    return this.http.post(this.baseUrl + 'table_booking_pdf_export_by_date', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
      responseType: 'blob',
      observe: 'response',

    });
  }
  exportCustomerConsentToExcel() {
    return this.http.get(this.baseUrl + 'export_customer_consent_excel', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
      responseType: 'blob',
      observe: 'response',

    });
  }
  
  gettableBookingsWithStatus(status) {
    return this.http.get(this.baseUrl + 'get_bookings_with_status?reservation_status=' + status, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updatetableBookingsWithStatus(id, status) {
    let payload = {
      reservation_id: id,
      status: status
    };
    return this.http.post(this.baseUrl + 'update_table_booking_status', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  assigntableBookingTable(id, table_id) {
    let payload = {
      reservation_id: id,
      table_id: table_id
    };
    return this.http.post(this.baseUrl + 'assign_table_booking_table', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getTablesList() {
    return this.http.get(this.baseUrl + 'table_list_to_admin', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getStaffList() {
    return this.http.get(this.baseUrl + 'staff_list', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getQuickSettings() {
    return this.http.get(this.baseUrl + 'getQuickSettings', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUserMetaOnlineSettings() {
    return this.http.get(this.baseUrl + 'get_user_meta_online_settings', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateQuickSettings(data: any) {
    return this.http.post(this.baseUrl + 'updateQuickSettings', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
    updateOnlineSettingsOnMetadata(data: any) {
    return this.http.post(this.baseUrl + 'update_online_settings_on_metadata', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateQuickSettingsSelections(data: any) {
    return this.http.post(this.baseUrl + 'updateQuickSettingsSelections', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateStaffClockTime(data: any) {
    return this.http.post(this.baseUrl + 'update_staff_clock_time', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateStaffBreakTime(data: any) {
    return this.http.post(this.baseUrl + 'update_staff_break_time', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }



  updateGateWayToStripe(data: any) {
    return this.http.post(this.baseUrl + 'update_gateway_stripe', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateGateWayToWindcave(data: any) {
    return this.http.post(this.baseUrl + 'update_gateway_windcave', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateGateWayToPayrix(data: any) {
    return this.http.post(this.baseUrl + 'update_gateway_payrix', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getUberTocken(data: any) {
    return this.http.post(this.baseUrl + 'get_uber_tocken', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUberStores(data: any) {
    return this.http.post(this.baseUrl + 'get_uber_store', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUberStoresToProvision(data: any) {
    return this.http.post(this.baseUrl + 'get_uber_store_to_provision', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteUberStoresProvision(data: any) {
    return this.http.post(this.baseUrl + 'delete_uber_store_provision', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  activateUberStore(data: any) {
    return this.http.post(this.baseUrl + 'provision_uber_store', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateUberStoreProvisioning(data: any) {
    return this.http.post(this.baseUrl + 'update_uber_store_provision', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUserId() {
    return this.http.get(this.baseUrl + 'get_uid', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  addUberStoreProvisioned(data: any) {
    return this.http.post(this.baseUrl + 'add_uber_store_provisioned', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  pauseUberEatsOrder(data: any) {
    return this.http.post(this.baseUrl + 'pause_uber_order', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  resumeUberEatsOrder() {
    return this.http.post(this.baseUrl + 'resume_uber_order', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  checkUberEatsStoreStatus() {
    return this.http.get(this.baseUrl + 'chek_uber_eats_store_status', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUberConnectedStores() {
    return this.http.get(this.baseUrl + 'get_uber_stores_provisioned', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUberStoreId() {
    return this.http.get(this.baseUrl + 'get_uber_store_id', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getUberStore(id) {
    return this.http.get(this.baseUrl + 'get_uber_store?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  uberMenuSyncNode(storeId: any) {
    let data = null;
    return this.http.put(this.baseUrlNode + 'uber/uber_menu_sync?storeId=' + storeId, data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  uberMenuSync(storeId:any){
      let data={
        "storeId": storeId
      };
    return this.http.post(this.baseUrl + 'update_uber_menu', data,{
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  retrieveUberMenu(storeId: any) {
    let data = {
      "storeId": storeId
    };
    return this.http.post(this.baseUrl + 'retrieve_uber_menu', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUberStoreDetails(storeId: any) {
    return this.http.get(this.baseUrl + 'get_uber_store_details?storeId=' + storeId, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateUberStoreStatus(storeId: any, status: string, pausedUntil?: string, reason?: string) {
    let data: any = {
      storeId: storeId,
      status: status
    };
    if (pausedUntil) {
      data.pausedUntil = pausedUntil;
    }
    if (reason) {
      data.reason = reason;
    }
    return this.http.post(this.baseUrl + 'update_uber_store_status', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getUberStoreStatus(storeId: any) {
    return this.http.get(this.baseUrl + 'get_uber_store_status?storeId=' + storeId, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateUberStorePrepTime(storeId: any, defaultPrepTime?: number, busyModeEnabled?: boolean, busyModePrepTime?: number) {
    let data: any = {
      storeId: storeId
    };
    if (defaultPrepTime !== undefined && defaultPrepTime !== null) {
      data.defaultPrepTime = defaultPrepTime;
    }
    if (busyModeEnabled !== undefined && busyModeEnabled !== null) {
      data.busyModeEnabled = busyModeEnabled;
    }
    if (busyModePrepTime !== undefined && busyModePrepTime !== null) {
      data.busyModePrepTime = busyModePrepTime;
    }
    return this.http.post(this.baseUrl + 'update_uber_store_prep_time', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateUberMenuRawEdit(storeId: any, finalmenus: any) {
    let data = {
      "storeId": storeId,
      "finalmenus": finalmenus
    };
    return this.http.post(this.baseUrl + 'update_uber_menu_raw_edit', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createCashInOutReason(data: any) {
    return this.http.post(this.baseUrl + 'cash_in_out_reason_create', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCashInOutReason() {
    return this.http.get(this.baseUrl + 'cash_in_out_reason_admin', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deteleCashInOutReason(data: any) {
    return this.http.post(this.baseUrl + 'cash_in_out_reason_delete', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  updateCashInOutReason(data: any) {
    return this.http.post(this.baseUrl + 'cash_in_out_reason_update', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  fileUploadS3(data: any) {
    return this.http.post(this.baseUrl + 'fileUpload_s3', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
      reportProgress: true,
      observe: 'events',
    });
  }

  getUserFiles() {
    return this.http.get(this.baseUrl + 'get_user_files', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getOnlineSettings() {
    return this.http.get(this.baseUrl + 'online_order_settings_get', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateOnlineOrderSettings(data: any) {
    return this.http.post(this.baseUrl + 'online_order_settings', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateloyaltySettings(data: any) {
    return this.http.post(this.baseUrl + 'update_loyality_enable_prod_level', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getloyaltySettings() {
    return this.http.get(this.baseUrl + 'get_loyality_enable_prod_level', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateShopLogo(data: any) {
    return this.http.post(this.baseUrl + 'updateShopLogo', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getShopLogo() {
    return this.http.get(this.baseUrl + 'getShopLogo', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  // customer display



  addCustomerDisplayimage(data: any) {
    return this.http.post(this.baseUrlNode + 'customer/customerdisplayim', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteCustomerDisplayimage(id: any) {
    return this.http.delete(this.baseUrlNode + 'customer/deletecustomerdisplayim/' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getCustomeDisplayImages() {
    return this.http.get(this.baseUrlNode + 'customer/getcustomerdisplayimages', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getIssues() {
    return this.http.get(this.baseUrl + 'get_issues', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getIssueById(id) {
    return this.http.get(this.baseUrl + 'get_issue_by_id?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createIssue(data: any) {
    return this.http.post(this.baseUrl + 'add_issue', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateIssue(data: any) {
    return this.http.post(this.baseUrl + 'update_issue', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateIssueThread(data) {

    return this.http.post(this.baseUrl + 'update_issue_thread', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  checkBarcode(code: any, id: any) {
    const fromdata = new FormData();
    if (id) {
      fromdata.append('id', id);
    }
    fromdata.append('code', code);
    return this.http.post(this.baseUrl + 'check_barcode', fromdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  createUpdateReportSettings(data: any) {

    return this.http.post(this.baseUrl + 'create_report_settings', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getReportSettings() {

    return this.http.get(this.baseUrl + 'get_report_settings', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


// sales category 


 getSalesCategories() {
    return this.http.get(this.baseUrl + 'get_sales_category', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createSalesCategory(data: any) {
    return this.http.post(this.baseUrl + 'create_sales_category', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

   updateSalesCategory(data: any) {
      return this.http.post(this.baseUrl + 'update_sales_category', data, {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
    }  


    deleteSalesCategory(data: any) {
      return this.http.post(this.baseUrl + 'delete_sales_category', data, {
        headers: new HttpHeaders({  
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      });
    }

  createSalesCatProdMapping(data: any) {
    return this.http.post(this.baseUrl + 'create_sales_cat_prod_mapping', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  createSalesCatProdMappingBulk(data: any) {
    return this.http.post(this.baseUrl + 'create_sales_cat_prod_mapping_bulk', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  //get_sales_cat_prod_mapping

  getSalesCatProdMapping() {
    return this.http.get(this.baseUrl + 'get_sales_cat_prod_mapping', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

 //sales_cat_prod_mapping_by_product_id

  getSalesCatProdMappingByProductId(id: any) {
    return this.http.get(this.baseUrl + 'sales_cat_prod_mapping_by_product_id?product_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  //sales_cat_prod_mapping_by_sales_cat_id

  getSalesCatProdMappingBySalesCatId(id: any) {
    return this.http.get(this.baseUrl + 'sales_cat_prod_mapping_by_sales_cat_id?sales_cat_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  //delete_sales_cat_prod_mapping

  deleteSalesCatProdMapping(id: any) {
    return this.http.get(this.baseUrl + 'delete_sales_cat_prod_mapping?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  

  getProductSizes(id: any) {
    return this.http.get(this.baseUrl + 'productsize_product?id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getAllProductSizes() {
    return this.http.get(this.baseUrl + 'productsize_all', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getProductSizesPaginated(payload: {
    per_page?: number;
    page?: number;
    search?: string;
    product_id?: number | string;
  }): Observable<any> {
    return this.http.post(this.baseUrl + 'productsize_search_paginated', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  updateProductOnlineMenuStatus(id: number | string, hidden: number | boolean) {
    return this.postOnlineMenuStatusUpdate('product_online_menu_status_update', id, hidden);
  }

  updateProductSizeOnlineMenuStatus(id: number | string, hidden: number | boolean) {
    return this.postOnlineMenuStatusUpdate('productsize_hide_online_menu', id, hidden);
  }

  updateModifierOnlineMenuStatus(id: number | string, hidden: number | boolean) {
    return this.postOnlineMenuStatusUpdate('modifier_hide_from_online_menu', id, hidden);
  }

  updateModifierCategoryOnlineMenuStatus(id: number | string, hidden: number | boolean) {
    return this.postOnlineMenuStatusUpdate('modifier_category_hide_from_online_menu', id, hidden);
  }

  updateComboOnlineMenuStatus(id: number | string, hidden: number | boolean) {
    return this.postOnlineMenuStatusUpdate('combo_online_menu_status_update', id, hidden);
  }

  updateCategoryOnlineMenuStatus(id: number | string, hidden: number | boolean) {
    return this.postOnlineMenuStatusUpdate('category_hide_from_online_menu', id, hidden);
  }

  private postOnlineMenuStatusUpdate(endpoint: string, id: number | string, hidden: number | boolean) {
    const formdata = new FormData();
    formdata.append('id', `${id}`);
    const status = typeof hidden === 'number' ? hidden : hidden ? 1 : 0;
    formdata.append('is_hidden_status', `${status}`);
    return this.http.post(this.baseUrl + endpoint, formdata, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getMenuTypes() {
    return this.http.get(this.baseUrl + 'get_menu_types', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getWhatsappAlerts() {
    return this.http.get(this.baseUrl + 'list_whatsapp_alert', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createWhatsappAlerts(data: any) {
    return this.http.post(this.baseUrl + 'create_whatsapp_alert', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  editWhatsappAlerts(data: any) {
    return this.http.post(this.baseUrl + 'edit_whatsapp_alert', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  deleteWhatsappAlert(data: any) {
    return this.http.post(this.baseUrl + 'delete_whatsapp_alert', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  excludeSizeModifier(data: any) {
    return this.http.post(this.baseUrl + 'exclude_modifier', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  undoexcludeSizeModifier(data: any) {
    return this.http.post(this.baseUrl + 'undo_exclude_modifier', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getLinkedShops() {
    return this.http.get(this.baseUrl + 'get_linked_shop_admin', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getLinkedWithMsaterShops() {
    return this.http.get(this.baseUrl + 'get_linked_shop_with_master_admin', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getTokenWithMaster(id) {
    return this.http.get(this.baseUrl + 'store_token_master?user_id=' + id, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  
  ticketInvoiceToMail(data) {
    return this.http.post(this.baseUrl + 'ticket_invoice_mail', data,{
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  createDataExclusion(data: any) {
    return this.http.post(this.baseUrl + 'dataexclusion_create', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  getDataExclusion(data: any) {
    return this.http.post(this.baseUrl + 'dataexclusion_get_by_item', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  getPaymentGateways() {
    return this.http.get(this.baseUrl + 'get_payment_gateways', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }

  configPaymentGateway(data: any) {
    return this.http.post(this.baseUrl + 'payment_gateway_config_create', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  configPaymentGatewayEdit(data: any) {
    return this.http.post(this.baseUrl + 'payment_gateway_config_edit', data, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }
  configPaymentGatewayDelete(data: any) {
    let payload = {
      id: data
    };
    return this.http.post(this.baseUrl + 'payment_gateway_config_delete', payload, {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }


  getCustomerPromotions() {
    return this.http.get(
      this.baseUrl +
      'customer-promotions-list',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  createCustomerPromotion(data) {
    return this.http.post(
      this.baseUrl +
      'customer-promotions-create', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }



  // product suggestions

  productSuggestionAdd(data) {
    return this.http.post(
      this.baseUrl +
      'product_suggestion_add_edit', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  cartSuggestionAdd(data) {
    return this.http.post(
      this.baseUrl +
      'cart_suggestion_create', data,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  productSuggestionByProduct(id) {
    return this.http.get(
      this.baseUrl +
      'product_suggestion_by_product?id='+id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  cartSuggestionByDomain(domain) {
    return this.http.get(
      this.baseUrl +
      'cart_suggestion_list?domain='+domain,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  productSuggestionListAll(id) {
    return this.http.get(
      this.baseUrl +
      'product_suggestion_list?id='+id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  productSuggestionDelete(id) {
    return this.http.get(
      this.baseUrl +
      'product_suggestion_delete?id='+id,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }


  //customer journel

  

  getCustomerJournelByCusID() {
    return this.http.get(
      this.baseUrl +
      'get_customer_journals',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getCustomerJournelTicketsToday() {
    return this.http.get(
      this.baseUrl +
      'todays_customer_journal_tickets',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getCustomerJournelTicketsByDate(from_date,to_date) {
    const start=moment(from_date).utc().format();
    const end=moment(to_date).utc().format();
    return this.http.get(
      this.baseUrl +
      'customer_journal_tickets_by_date?from_date=' + start + '&to_date=' + end,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getCustomerJournelsToday() {
    return this.http.get(
      this.baseUrl +
      'todays_customer_journal',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getCustomerJournelsByDate(from_date,to_date) {
    const start=moment(from_date).utc().format();
    const end=moment(to_date).utc().format();
    return this.http.get(
      this.baseUrl +
      'customer_journal_by_date?from_date=' +start+ '&to_date=' + end,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }

  getCustomerBalances() {
    return this.http.get(
      this.baseUrl +
      'customer_journal_customer_balance',
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getCustomerJournelTicketsByCusID(id,start,end) {
    return this.http.get(
      this.baseUrl +
      'customer_journal_tickets_by_cust_id?customer_id=' + id+'&from_date='+moment(start).utc().format()+'&to_date='+moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }
  getCustomerJournels(id,start,end) {
   
    return this.http.get(
      this.baseUrl +'get_customer_journals?customer_id=' + id+'&from_date='+moment(start).utc().format()+'&to_date='+moment(end).utc().format(),
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.get().getValue(),
        }),
      },
    );
  }



  getAvailableDeliveryServices(){
    return this.http.get(this.baseUrl + 'get_available_delivery_services', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }






  getActivityLogs(){
    return this.http.get(this.baseUrl + 'data-operation-logs', {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.get().getValue(),
      }),
    });
  }





  // end ########################################################################################3

  setStaffLoginRequired(required: boolean): void {
    localStorage.setItem('is_staff_login', required ? 'true' : 'false');
    if (!required) {
      this.setStaffPinVerified(false);
    }
  }

  setStaffPinVerified(verified: boolean): void {
    if (verified) {
      localStorage.setItem('staff_pin_verified', 'true');
    } else {
      localStorage.removeItem('staff_pin_verified');
    }
  }

  isStaffPinVerified(): boolean {
    return localStorage.getItem('staff_pin_verified') === 'true';
  }

  getCurrentAdminPermissions(): string[] {
    const user = this.loginUser.getValue() ?? this.getAccessUser();
    let permissions = user?.admin_permission ?? user?.admin_permissions ?? [];

    if (typeof permissions === 'string') {
      try {
        permissions = JSON.parse(permissions);
      } catch (error) {
        permissions = [permissions];
      }
    }

    if (!Array.isArray(permissions)) {
      return [];
    }

    return permissions
      .filter((permission) => permission !== undefined && permission !== null)
      .map((permission) => `${permission}`.trim());
  }

  hasAdminPermission(permission: string): boolean {
    if (!permission) {
      return true;
    }

    const permissions = this.getCurrentAdminPermissions().map((value) => value.toLowerCase());
    if (permissions.includes('all')) {
      return true;
    }

    return permissions.includes(permission.toLowerCase());
  }

  /**
   * Finds the first accessible route based on user permissions.
   * Priority: Dashboard first, then first available route from menu items.
   * @returns Route path string (e.g., '/pages/dashboard')
   */
  getFirstAccessibleRoute(): string {
    // First priority: Check if user has permission for Dashboard
    if (this.hasAdminPermission('Dashboard')) {
      return '/pages/dashboard';
    }

    // If not, find the first route from menu items that user has permission for
    // Import MENU_ITEMS dynamically to avoid circular dependencies
    const menuItems = this.getMenuItemsForRouteFinding();
    const firstRoute = this.findFirstAccessibleRouteInMenu(menuItems);
    
    return firstRoute || '/pages/dashboard'; // Fallback to dashboard if nothing found
  }

  /**
   * Helper method to get menu items for route finding.
   */
  private getMenuItemsForRouteFinding(): any[] {
    try {
      return MENU_ITEMS || [];
    } catch (error) {
      console.error('Error loading menu items:', error);
      return [];
    }
  }

  /**
   * Recursively finds the first accessible route in menu items.
   * @param items - Menu items to search through
   * @param parentPermissionKey - Permission key from parent item (for children that inherit permissions)
   */
  private findFirstAccessibleRouteInMenu(items: any[], parentPermissionKey?: string): string | null {
    for (const item of items) {
      // Get permission key from item or use parent's permission key
      const permissionKey = item.data?.permissionKey || parentPermissionKey;
      
      // Check if this item has a link
      if (item.link) {
        // If no permission key required (undefined), or user has permission, return this route
        if (!permissionKey || this.hasAdminPermission(permissionKey)) {
          return item.link;
        }
      }

      // If item has children, recursively search with this item's permission key as parent
      // Children inherit permission from parent if they don't have their own
      if (item.children && Array.isArray(item.children)) {
        const childRoute = this.findFirstAccessibleRouteInMenu(item.children, permissionKey);
        if (childRoute) {
          return childRoute;
        }
      }
    }

    return null;
  }

  getStaffPinExpiryData(): { expiry: number; minutes: number } | null {
    const encoded = localStorage.getItem('staff_pin_expiry');
    if (!encoded) {
      return null;
    }

    try {
      const decoded = atob(encoded);
      const payload = JSON.parse(decoded);
      const expiryTime = new Date(payload?.expiry).getTime();
      const minutes = Number(payload?.minutes ?? payload?.admin_staff_logout_time);

      if (!expiryTime || Number.isNaN(expiryTime)) {
        this.clearStaffPinExpiry();
        return null;
      }

      return {
        expiry: expiryTime,
        minutes: Number.isFinite(minutes) ? minutes : 0,
      };
    } catch (error) {
      console.error('Failed to parse staff pin expiry payload', error);
      this.clearStaffPinExpiry();
      return null;
    }
  }

  /**
   * Sets or clears the staff PIN expiry based on the provided minutes.
   * - minutes > 0  → store new expiry starting from "now"
   * - minutes <= 0 → clear expiry
   */
  setStaffPinExpiry(minutes: number | null | undefined): void {
    const parsedMinutes = Number(minutes);

    if (!Number.isFinite(parsedMinutes) || parsedMinutes <= 0) {
      this.clearStaffPinExpiry();
      return;
    }

    const expiresAt = new Date(Date.now() + parsedMinutes * 60000);
    const payload = {
      expiry: expiresAt.toISOString(),
      minutes: parsedMinutes,
    };

    try {
      const encoded = btoa(JSON.stringify(payload));
      localStorage.setItem('staff_pin_expiry', encoded);
    } catch (error) {
      console.error('Failed to encode staff pin expiry', error);
      this.clearStaffPinExpiry();
    }
  }

  isStaffPinExpired(): boolean {
    const data = this.getStaffPinExpiryData();
    if (!data) {
      return false;
    }
    return Date.now() >= data.expiry;
  }

  clearStaffPinExpiry(): void {
    localStorage.removeItem('staff_pin_expiry');
  }

  expireStaffPinSession(): void {
    this.clearStaffPinExpiry();
    this.setStaffPinVerified(false);
    this.clearAccessUser(false);
    //localStorage.removeItem('sipo_user');
    this.updateLoggedUser();
    this.updateSipoUser();
  }

  private encodeBase64(value: string): string {
    try {
      return btoa(unescape(encodeURIComponent(value)));
    } catch (error) {
      return btoa(value);
    }
  }

  private decodeBase64(value: string): string {
    try {
      return decodeURIComponent(escape(atob(value)));
    } catch (error) {
      return atob(value);
    }
  }

  private decodeAccessUserPayload(raw: string | null): any {
    if (!raw) {
      return null;
    }

    const tryParse = (input: string | null) => {
      if (!input) {
        return null;
      }
      try {
        return JSON.parse(input);
      } catch (error) {
        return null;
      }
    };

    try {
      const decoded = this.decodeBase64(raw);
      const parsed = tryParse(decoded);
      if (parsed !== null) {
        return parsed;
      }
    } catch (error) {
      // ignore and fallback
    }

    return tryParse(raw);
  }

  private safeParseJson(value: string | null): any {
    if (!value) {
      return null;
    }
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  normalizeStaffPinUser(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }
    const normalized: any = { ...data };

    if (typeof normalized.permissions === 'string') {
      try {
        normalized.permissions = JSON.parse(normalized.permissions);
      } catch (e) {
        normalized.permissions = normalized.permissions;
      }
    }

    if (typeof normalized.admin_permissions === 'string') {
      try {
        normalized.admin_permissions = JSON.parse(normalized.admin_permissions);
      } catch (e) {
        normalized.admin_permissions = normalized.admin_permissions;
      }
    }

    if (!normalized.admin_permission && normalized.admin_permissions) {
      normalized.admin_permission = normalized.admin_permissions;
    }

    return normalized;
  }

}
